import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { offerShortDescription } from '@webbuilder/schemas'
import { OfferDescriptionFields } from './OfferDescriptionFields'
import { MembershipInclusionsPicker, inclusionsValid, type InclusionDraft } from './MembershipInclusionsPicker'
import { Notice } from '../Notice'
import { activeLanguage, useI18n, translate } from '../../../lib/i18n'
import { WizardTopBar } from '../WizardTopBar'
import { PendingChangesBar } from '../../DashboardTopbarActions'
import type { Translate } from '@webbuilder/i18n'
import { StripeMark } from './StripeMark'
import { StripeOnboardingDialog } from './StripeOnboardingDialog'
import { PublishBlockDialog } from './OfferStatusSelect'
import { PAYMENTS_UPSELL, UpsellCard } from '../FeatureUpsell'
import { PlanLimitModal } from '../PlanLimitModal'
import { reachedLimit, SITE_PLAN_LIMITS } from '../../../lib/planLimits'
import type { BillingPlan } from '../../../lib/api'
import { createPortal } from 'react-dom'
import { IconAlertTriangle, IconArrowLeft, IconAdjustments, IconAt, IconBell, IconPhone, IconEye, IconListDetails, IconForms, IconInfoCircle, IconCalendarOff, IconChevronDown, IconCoin, IconLifebuoy, IconArrowRight, IconCalendarEvent, IconCheck, IconClock, IconLoader2, IconPencil, IconPlus, IconUser, IconUsers, IconWorld, IconX } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { addOfferPrice, createBookingResource, getBookingInclusions, createOffer, removeOfferPrice, saveBookingInclusions, saveBookingSchedule, updateOffer, type Booking, type BookingException, type BookingFormField, type BookingAssignment, type BookingResource, type BookingRule, type Offer } from '../../../lib/api'
import { computeDaySlots, computeSlots } from '@webbuilder/booking'
import { generateSlug } from '../../PageManager/types'
import { OfferGalleryField, PreviewGallery } from './OfferGalleryField'
import { BookingFormFieldsEditor, TYPE_ICON, isCompleteFormField } from './BookingFormFieldsEditor'
import { bookingError } from './bookingShared'
import { useStripeConnect } from '../../../lib/stripeConnect'
import { formatMoney, minorUnitFactor, toMinorUnits } from './offerShared'
import { useRegion } from '../../../lib/region'
import { DEFAULT_PAGE_THEME } from '@webbuilder/theme'
import { useScrollTopOnStep } from '../../../lib/useScrollTopOnStep'
import { OfferCustomFieldsEditor } from './OfferCustomFields'
import { InfoHint } from '../../SettingsControls'
import { TeamPhotoButton } from './TeamPhotoButton'

const PRESETS = [15, 30, 45, 60, 90, 120]
/* Las constantes de módulo guardan la CLAVE y no el texto: aquí no se puede
   llamar a un hook, así que quien las pinta las pasa por su `t`. */
const WEEKDAYS = ['svcw.weekday.0', 'svcw.weekday.1', 'svcw.weekday.2', 'svcw.weekday.3', 'svcw.weekday.4', 'svcw.weekday.5', 'svcw.weekday.6']
/*
 * Precio en tercer lugar y no en quinto.
 *
 * Es un dato del servicio, como su duración o su cupo: se decide con ellos y de
 * un tirón. Estaba detrás del formulario y del horario, que son las dos partes
 * largas del alta, y eso obligaba a atravesarlas para contestar algo que ya se
 * sabía desde el principio.
 */
/**
 * Los pasos, cada uno con su icono.
 *
 * ── Por qué icono y no número ───────────────────────────────────────────────
 * El número decía en qué posición va un paso, que es justo lo que no hace falta
 * saber: nadie busca «el cuarto», busca «donde se pone el precio». Y editando
 * era además una cuenta de nada, porque los siete existen desde el principio y
 * se entra al que toque. El icono dice qué es, que es lo que se busca, y sigue
 * diciéndolo cuando la ventana se estrecha y las etiquetas se esconden —donde
 * antes quedaban siete círculos con un número y ninguna pista—.
 *
 * En la misma lista y no en una tabla aparte a propósito: separados, añadir un
 * paso sin su icono no daría ningún error y saldría un hueco en la barra.
 */
const STEPS = [
  { key: 'svcw.step.info', Icon: IconInfoCircle },
  { key: 'svcw.step.settings', Icon: IconAdjustments },
  { key: 'svcw.step.price', Icon: IconCoin },
  { key: 'svcw.step.form', Icon: IconForms },
  { key: 'svcw.step.schedule', Icon: IconCalendarEvent },
  { key: 'svcw.step.cancel', Icon: IconCalendarOff },
  /* Lo propio de este servicio que su página enseña («Qué incluye»…). Ver
     `OfferCustomFields`. Opcional. */
  { key: 'offers.new.step.extra', Icon: IconListDetails },
  { key: 'svcw.step.review', Icon: IconEye },
] as const

/**
 * A qué parte de la página mira la vista previa en cada paso.
 *
 * La maqueta entera cabe en la columna, pero entera es también ilegible: el
 * título del servicio mide once píxeles y quien está escribiéndolo no puede
 * comprobar si cabe, si se corta o si dice lo que quería. Acercarse a la zona
 * que se está editando es lo que convierte la vista previa en algo que se mira
 * de verdad en vez de un adorno.
 *
 * `full` en el último paso a propósito: revisar es lo contrario de editar —ahí
 * lo que hace falta es ver la página completa, que es lo que verá el cliente.
 */
/* Ajustes mira a `facts` y no a `info`: lo que se toca ahí —duración y cupo—
   sale en la línea de datos del pie de la ficha, no en el bloque del título. Con
   `info` la cámara se quedaba arriba, enseñando el nombre mientras se elegían
   los minutos. */
/* «Cancelación» mira a la página entera: su política no se pinta en un sitio
   concreto de la maqueta, es una promesa sobre todo lo demás. */
const PREVIEW_FOCUS = ['info', 'facts', 'price', 'form', 'schedule', 'full', 'full', 'full'] as const

/**
 * Las zonas a las que puede mirar la vista previa.
 *
 * El paso da la zona ancha —«la información», «el horario»— y el campo que se
 * está escribiendo la afina: pulsar «Nombre» lleva al título, «Descripción» al
 * párrafo. Es lo que responde la pregunta que uno se hace al escribir en un
 * asistente: «¿esto dónde acaba saliendo?».
 */
type PreviewZone = 'info' | 'media' | 'title' | 'desc' | 'details' | 'price' | 'facts' | 'form' | 'schedule' | 'full'

/**
 * Cuánto se acerca cada zona. El encuadre ya no está aquí: se mide.
 *
 * Las pequeñas piden más aumento —un título de once píxeles no se lee al 1,4—
 * y las que son un bloque entero, menos, o se pierde el contexto de alrededor.
 */
const ZONE_SCALE: Record<Exclude<PreviewZone, 'full'>, number> = {
  info: 1.25,
  /* La portada, que es la cabecera de la ficha. Acompañaba al logo, que se
     elegía justo al lado; desde que la marca se configura en «Apariencia», aquí
     sólo queda la foto de lo que se vende. */
  media: 1.35,
  title: 1.4,
  desc: 1.35,
  details: 1.1,
  facts: 1.4,
  price: 1.4,
  schedule: 1.2,
  form: 1.25,
}
/*
 * «Gratis» y «En el lugar» son el mismo modo para el servidor (`onsite`: la
 * reserva no pasa por la caja). Lo que los separa es el precio: en el lugar
 * se enseña lo que se pagará allí; gratis no tiene. Juntos en una opción, quien
 * cobraba en el local no tenía dónde poner cuánto.
 */
type PayChoice = 'free' | Offer['booking']['paymentMode']
const PAYMENT_MODES: { value: PayChoice; label: string; hint: string }[] = [
  { value: 'free', label: 'svcw.pay.free', hint: 'svcw.pay.freeHint' },
  { value: 'onsite', label: 'svcw.pay.onsite', hint: 'svcw.pay.onsiteHint' },
  { value: 'prepaid', label: 'svcw.pay.prepaid', hint: 'svcw.pay.prepaidHint' },
  { value: 'deposit', label: 'svcw.pay.deposit', hint: 'svcw.pay.depositHint' },
  /* Fase 7 de las cuentas de clientes: la reservan quienes tienen una de las
     membresías o paquetes elegidos. Desde Pro (lo dice el propio selector). */
  { value: 'membership', label: 'svcw.pay.membership', hint: 'svcw.pay.membershipHint' },
]

function durationLabel(minutes: number, t: Translate) {
  if (minutes < 60) return t('svcw.dur.minutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? t('svcw.dur.hoursMinutes', { hours, minutes: rest }) : t('svcw.dur.hours', { count: hours })
}
/** Por días: «Día completo», «3 días» o «1–7 días» si los elige el cliente. */
function daysLabel(min: number, max: number, t: Translate) {
  if (max > min) return t('svcw.days.range', { min, max })
  return min === 1 ? t('svcw.days.full') : t('svcw.days.count', { count: min })
}
function minutesToTime(minute: number) {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
}
function timeToMinutes(value: string) {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  if (!match) return null
  const result = Number(match[1]) * 60 + Number(match[2])
  return result >= 0 && result <= 1440 ? result : null
}

/**
 * Safari móvil pinta `input[type=time]` dentro de una caja nativa que puede
 * ignorar el tamaño tipográfico del formulario. Este envoltorio conserva ese
 * input —y por tanto su selector de hora accesible— pero permite mostrar encima
 * un valor propio y compacto cuando CSS detecta una pantalla estrecha.
 */
function TimeControl({ value, label, onChange }: {
  value: string
  label: string
  onChange: (value: string) => void
}) {
  const minute = timeToMinutes(value)
  const display = minute === null
    ? value
    : new Intl.DateTimeFormat(activeLanguage(), { hour: 'numeric', minute: '2-digit' })
      .format(new Date(2020, 0, 1, Math.floor(minute / 60), minute % 60))

  return (
    <span className="dash-svc-time-control">
      <span className="dash-svc-time-control-value" aria-hidden="true">{display}</span>
      <input type="time" aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} />
    </span>
  )
}

const ASSIGNMENTS: BookingAssignment[] = ['least_busy', 'order', 'round_robin']

function initialRules(resource?: BookingResource): BookingRule[] {
  if (resource?.rules.length) return resource.rules.map(({ weekday, startMinute, endMinute }) => ({ weekday, startMinute, endMinute }))
  return [1, 2, 3, 4, 5].map((weekday) => ({ weekday, startMinute: 540, endMinute: 1020 }))
}

type SimpleSchedule = {
  weekdays: number[]
  startMinute: number
  endMinute: number
}

/**
 * El editor simple sólo promete una franja compartida por todos los días.
 *
 * Si ya hay horas distintas o dos turnos en un día, devuelve `null`: esa agenda
 * sigue intacta y se edita en «Más opciones». Una lista vacía sí es simple; así
 * se puede cerrar el último día y volver a abrir otro sin quedar atrapado.
 */
function simpleSchedule(rules: BookingRule[]): SimpleSchedule | null {
  if (!rules.length) return { weekdays: [], startMinute: 540, endMinute: 1020 }
  const first = rules[0]
  if (!first) return null
  const weekdays = new Set<number>()
  for (const rule of rules) {
    if (weekdays.has(rule.weekday)) return null
    if (rule.startMinute !== first.startMinute || rule.endMinute !== first.endMinute) return null
    weekdays.add(rule.weekday)
  }
  return { weekdays: [...weekdays], startMinute: first.startMinute, endMinute: first.endMinute }
}

/**
 * La zona del navegador de quien está configurando esto.
 *
 * Se lee una vez y no en cada dibujado: no cambia mientras la pestaña está
 * abierta, y `resolvedOptions()` no es gratis.
 */
const READER_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
})()

/** `America/Los_Angeles` → `Los Angeles (GMT-7)`. */
function zoneLabel(zone: string): string {
  /* Sólo la ciudad: el continente no aporta —nadie duda de en qué continente
     está Los Ángeles— y con él la frase se llenaba de barras y guiones bajos. */
  const city = (zone.split('/').pop() ?? zone).replace(/_/g, ' ')
  try {
    const parts = new Intl.DateTimeFormat(activeLanguage(), { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date())
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value
    return offset ? `${city} (${offset})` : city
  } catch {
    return city
  }
}

/* Lo que mide el banner de una cita, en píxeles. No se lee del DOM porque hay
   que decidir si cabe encima ANTES de pintarlo; van holgados a propósito. */
const PEEK_WIDTH = 240
const PEEK_HEIGHT = 104

/**
 * Las tres cosas que puede decir una excepción.
 *
 * El motor sólo entiende dos verbos —`closed` resta horas del día y `open` las
 * suma—, pero con dos etiquetas la de abrir se leía como una restricción. Estas
 * tres los combinan de forma que lo que se lee es lo que pasa.
 */
const EXCEPTION_MODES = {
  'closed-day': { kind: 'closed', startMinute: null, endMinute: null },
  'closed-range': { kind: 'closed', startMinute: 840, endMinute: 1080 },
  /* Con horas desde el principio: un `open` sin ellas el motor lo ignora, y el
     día se quedaba igual con cara de haber cambiado. */
  open: { kind: 'open', startMinute: 540, endMinute: 1020 },
} as const satisfies Record<string, Pick<BookingException, 'kind' | 'startMinute' | 'endMinute'>>

type ExceptionMode = keyof typeof EXCEPTION_MODES

const exceptionMode = (exception: BookingException): ExceptionMode =>
  exception.kind === 'open' ? 'open' : exception.startMinute === null ? 'closed-day' : 'closed-range'

/** Una cita ya vendida, tal y como la enseña el probador de horarios. */
type TakenSlot = {
  id: string
  time: string
  endTime: string
  name: string
  email: string
  phone: string
  status: 'held' | 'confirmed' | 'completed'
}

const TAKEN_LABEL: Record<TakenSlot['status'], string> = {
  held: 'svcw.taken.held',
  confirmed: 'svcw.taken.confirmed',
  completed: 'svcw.taken.completed',
}

export function NewBookingServiceDialog({ projectId, token, siteOrigin, timezone, resources, service, initialStep = 0, bookings = [], onClose, onCreated, onResourcesChange, canConnectStripe = true, plan, publishedOffers, onOpenPlans }: {
  projectId: string
  token: string
  siteOrigin?: string | null
  timezone: string
  /** El plan y las ofertas publicadas que ya tiene: con los dos se sabe, antes
   *  de pulsar, si «publicado» cabe. El cupo es del catálogo entero, no de la
   *  agenda — ver `PlanLimits.offers`. */
  plan: BillingPlan
  publishedOffers: number
  resources: BookingResource[]
  /**
   * El servicio que se está editando, o ausente para crear uno nuevo.
   *
   * El mismo asistente para las dos cosas, y no dos pantallas parecidas: los
   * campos, el orden, las validaciones y la vista previa son idénticos, así que
   * duplicarlos garantizaba que un día divergieran — que el alta pidiera algo
   * que la edición no deja cambiar, o al revés.
   */
  service?: Offer | null
  /** Por qué paso abrir. Sólo se usa al editar: crear empieza por el principio. */
  initialStep?: number
  /**
   * Las citas ya vendidas de este servicio, para que el probador enseñe los
   * horarios que quedan DE VERDAD.
   *
   * Sin ellas los márgenes parecen rotos: apartan tiempo alrededor de las citas
   * que hay, y sobre una agenda vacía no hay nada alrededor de lo que apartar,
   * así que subir el margen no movía un solo horario. Al crear un servicio la
   * lista está vacía y eso es correcto —todavía no ha reservado nadie.
   */
  bookings?: Booking[]
  onClose: () => void
  onCreated: (service: Offer) => void
  onResourcesChange?: (resources: BookingResource[]) => void
  /** Si el plan permite conectar Stripe. Ver `assertCanConnectStripe`. */
  canConnectStripe?: boolean
  onOpenPlans?: () => void
}) {
  const { t } = useI18n()
  const editing = Boolean(service)
  /* «Por completar»: la empezó la IA con la agenda por defecto. Se recorre
     entera y en orden, como al crear: el horario y el precio los tiene que
     confirmar quien da el servicio. */
  const completing = Boolean(service?.setupPending)
  /*
   * El equipo: quiénes dan el servicio y de quién es el horario que se edita.
   *
   * Con una sola persona no cambia nada —su horario es «el horario»—. Con
   * varias, el servicio elige quiénes lo dan y el paso Horario edita el de
   * cada uno por turnos (cada persona tiene UNO, compartido por todos sus
   * servicios). Lo editado de cada uno se recuerda al cambiar de persona y se
   * guarda todo junto al final.
   */
  const activeTeam = resources.filter((row) => row.active)
  const [initialTeam] = useState<string[]>(() => {
    const listed = (service?.booking.resourceIds ?? []).filter((id) => activeTeam.some((row) => row.id === id))
    if (listed.length) return listed
    const single = service?.booking.resourceId
    if (single && activeTeam.some((row) => row.id === single)) return [single]
    const first = activeTeam[0]?.id ?? resources[0]?.id
    return first ? [first] : []
  })
  const [teamIds, setTeamIds] = useState<string[]>(initialTeam)
  const [scheduleOwnerId, setScheduleOwnerId] = useState(initialTeam[0] ?? '')
  const resource = resources.find((row) => row.id === scheduleOwnerId) ?? resources[0]
  const editedSchedules = useRef<Record<string, { rules: BookingRule[]; exceptions: BookingException[] }>>({})
  /* La zona en la que se guardan estos horarios: la del recurso si tiene una
     propia, si no la del sitio. Es la misma que usa el cálculo de huecos. */
  const scheduleZone = resource?.timezone ?? timezone

  const [step, setStep] = useState(completing ? 0 : initialStep)

  /* Abrir los planes en una ventana nueva para no perder el progreso del asistente:
     si se navegase dentro de la misma pantalla, el asistente se desmontaría y con
     él todos los campos, horarios y configuración que se acaban de rellenar. */
  const handleOpenPlans = useCallback(() => {
    const url = `${window.location.pathname}?project=${encodeURIComponent(projectId)}&view=plans`
    const opened = window.open(url, '_blank')
    if (!opened && onOpenPlans) {
      onOpenPlans()
    }
  }, [projectId, onOpenPlans])

  const railRef = useRef<HTMLDivElement | null>(null)
  const stripRef = useRef<HTMLDivElement | null>(null)
  /* La rejilla de horas —referencia del banner— y el cuerpo que recorta. */
  const timesRef = useRef<HTMLDivElement | null>(null)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  /* En qué campo se está escribiendo. `null` = en ninguno, y entonces manda la
     zona del paso. Se suelta al salir del campo para que la vista vuelva a la
     zona ancha en vez de quedarse clavada en el último sitio tocado. */
  const [focusZone, setFocusZone] = useState<PreviewZone | null>(null)
  const zoom: PreviewZone = focusZone ?? PREVIEW_FOCUS[step] ?? 'full'


  /*
   * Lo guardado siembra el formulario.
   *
   * En los inicializadores y no en un efecto: con un efecto el diálogo se pinta
   * un fotograma vacío y luego se rellena solo, que se lee como si hubiera
   * perdido los datos.
   */
  const [title, setTitle] = useState(service?.title ?? '')
  const [description, setDescription] = useState(service?.description ?? '')
  const [shortDescription, setShortDescription] = useState(service ? offerShortDescription(service) : '')
  /* La galería entera: la primera es la portada. Ver `OfferGalleryField`. */
  const [images, setImages] = useState<string[]>(service?.images ?? [])
  /* Los campos personalizados de las reservas, por id. Ver `OfferCustomFields`. */
  const [customValues, setCustomValues] = useState<Record<string, string>>(service?.customValues ?? {})
  const imageUrl = images[0] ?? ''
  /* El logo se quitó de aquí: la marca de la página se elige en «Apariencia»,
     y tenerla en dos sitios obligaba a saber cuál de los dos manda. Lo ya
     guardado no se toca — el alta deja de ENVIAR el campo, y el servidor sólo
     escribe los que recibe (ver `patchValues` en el worker). */
  /**
   * Para quién está abierto el selector de imágenes.
   *
   * Un nombre y no un booleano: el diálogo es el mismo y lo único que cambia es
   * dónde cae lo elegido, así que añadir otro campo que lo abra no obliga a
   * tocar esto. Llegó a haber dos —portada y logo— hasta que la marca se mudó a
   * «Apariencia».
   */
  const [duration, setDuration] = useState(service?.booking.durationMinutes ?? 60)
  const [capacity, setCapacity] = useState(service?.booking.capacity ?? 1)
  const [capacityDraft, setCapacityDraft] = useState<string | null>(null)
  /* Cuántas personas por reserva en un servicio con cupo, y si el precio va por
     persona. Con cupo 1 no se enseñan: una cita de uno es de uno. */
  const [maxParty, setMaxParty] = useState(service?.booking.maxParty ?? 1)
  /* Cuántos lugares quedan, en la página: siempre, sólo cuando quedan pocos
     (15 % o menos; «Quedan pocos» empuja a decidir) o nunca. */
  const [spotsDisplay, setSpotsDisplay] = useState<'all' | 'low' | 'none'>(service?.booking.spotsDisplay ?? 'low')
  /* Por horas o por días (fotógrafos, eventos, renta). Por días no hay horas:
     la reserva va de medianoche a medianoche y el horario sólo dice qué días
     se trabaja. */
  const [unit, setUnit] = useState<'time' | 'day'>(service?.booking.unit ?? 'time')
  const [daysMin, setDaysMin] = useState(service?.booking.daysMin ?? 1)
  const [daysMax, setDaysMax] = useState(service?.booking.daysMax ?? service?.booking.daysMin ?? 1)
  const flexDays = daysMax > daysMin
  /* «Otro»: un número fijo de días que no está en las pastillas. Abierto solo
     si lo guardado ya es uno de esos. */
  const [customDaysOpen, setCustomDaysOpen] = useState(() => {
    const min = service?.booking.daysMin ?? 1
    const max = service?.booking.daysMax ?? min
    return service?.booking.unit === 'day' && max === min && ![1, 2, 3, 7].includes(min)
  })
  const lengthLabel = (tr: Translate) => (unit === 'day' ? daysLabel(daysMin, daysMax, tr) : durationLabel(duration, tr))
  /* Quién atiende con «Cualquiera disponible». Por servicio y no del equipo:
     uno puede ir primero a la más experta y otro repartirse por turnos. */
  const [assignment, setAssignment] = useState<BookingAssignment>(service?.booking.assignment ?? 'least_busy')
  const [customDuration, setCustomDuration] = useState('')
  const [customDurationOpen, setCustomDurationOpen] = useState(false)
  const [formFields, setFormFields] = useState<BookingFormField[]>(service?.booking.formFields ?? [])
  /**
   * Preguntas a medias, las que el editor NO manda.
   *
   * Una pregunta sin texto no puede guardarse, así que el editor se la queda y
   * `formFields` no la ve. Sin este contador, «Siguiente» pasaba de largo y la
   * pregunta que estabas escribiendo se quedaba atrás sin decir nada — el peor
   * final posible, porque el trabajo se pierde en silencio y en el resumen no
   * hay hueco donde notar que falta.
   */
  const [preguntasAMedias, setPreguntasAMedias] = useState(0)
  /* Se marcan en rojo sólo cuando alguien ya intentó avanzar: antes de eso,
     señalar una pregunta recién añadida es regañar por no haber escrito todavía. */
  const [señalarPreguntas, setSeñalarPreguntas] = useState(false)
  const [paymentChoice, setPaymentChoice] = useState<PayChoice>(() => {
    const mode = service?.booking.paymentMode ?? 'onsite'
    if (mode !== 'onsite') return mode
    const hasPrice = (service?.prices ?? []).some((row) => row.active && row.unitAmount > 0)
    return hasPrice ? 'onsite' : 'free'
  })
  const paymentMode: Offer['booking']['paymentMode'] = paymentChoice === 'free' ? 'onsite' : paymentChoice
  /* Las membresías y paquetes que la incluyen; nulo hasta que se cargan. */
  const [inclusions, setInclusions] = useState<InclusionDraft[] | null>(null)
  /* «Sólo miembros»: nadie la reserva pagando. */
  const included = paymentChoice === 'membership'
  /* Mixto: se cobra como siempre y, además, la reservan sin pagar quienes
     tengan una de las membresías o paquetes elegidos. */
  const [alsoIncluded, setAlsoIncluded] = useState(false)
  /* Si ya la incluía alguna (para quitarlas sólo cuando se apaga, nunca a ciegas). */
  const [hadInclusions, setHadInclusions] = useState(service?.booking.paymentMode === 'membership')
  const canAlsoInclude = paymentChoice === 'onsite' || paymentChoice === 'prepaid' || paymentChoice === 'deposit'
  const mixed = canAlsoInclude && alsoIncluded
  /* Editando: si ya la incluye alguna, la casilla nace marcada. */
  useEffect(() => {
    if (!service || service.booking.paymentMode === 'membership') return
    let alive = true
    getBookingInclusions(projectId, service.id, token)
      .then((result) => { if (alive && result.chosen.length) { setAlsoIncluded(true); setHadInclusions(true) } })
      .catch(() => undefined)
    return () => { alive = false }
    // Sólo al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  /* El importe, de vuelta a como se escribe: lo guardado son centavos y aquí se
     teclean unidades. La misma cuenta que se hace al guardar, al revés. */
  const [amount, setAmount] = useState(() => {
    const price = service?.prices.find((row) => row.active && row.isDefault) ?? service?.prices.find((row) => row.active)
    if (!price) return ''
    return String(price.unitAmount / minorUnitFactor(price.currency))
  })
  /*
   * Los ajustes finos, como TEXTO y no como número.
   *
   * Vacío tiene que poder existir mientras se escribe —borrar para teclear otra
   * cosa deja el campo en blanco un instante— y con `number` en el estado ese
   * blanco se convierte en 0 y el cursor salta al final. Se convierten al
   * guardar, no en cada tecla.
   */
  const [stepMinutes, setStepMinutes] = useState(service?.booking.stepMinutes ? String(service.booking.stepMinutes) : '')
  const [bufferBefore, setBufferBefore] = useState(String(service?.booking.bufferBeforeMinutes ?? 0))
  const [bufferAfter, setBufferAfter] = useState(String(service?.booking.bufferAfterMinutes ?? 0))
  const [minNotice, setMinNotice] = useState(String(service?.booking.minNoticeMinutes ?? 0))
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(String(service?.booking.maxAdvanceDays ?? 30))
  /*
   * Hasta cuándo puede el cliente cancelar o mover su cita.
   *
   * En texto y no en número, como el resto de los ajustes finos: vacío tiene que
   * poder existir mientras se escribe, y un `number` obliga a inventarse un cero
   * en cuanto se borra el campo.
   */
  const [cancelWindow, setCancelWindow] = useState(String(service?.booking.cancelWindowHours ?? 24))
  /* Vacío = la misma que cancelar. Es lo que quiere casi todo el mundo, y dos
     números distintos son dos números que nadie recuerda haber puesto. */
  const [moveWindow, setMoveWindow] = useState(
    service?.booking.rescheduleWindowHours == null ? '' : String(service.booking.rescheduleWindowHours),
  )
  /* El recordatorio por correo. Encendido en los servicios nuevos, como lo crea
     la base: es lo que evita ausencias y casi nadie lo quiere apagado. */
  const [reminderEnabled, setReminderEnabled] = useState(service?.booking.reminderEnabled ?? true)
  const [reminderHours, setReminderHours] = useState(String(service?.booking.reminderHours ?? 24))
  /* Y qué se le ofrece DESPUÉS del plazo, que es donde se juega la relación. */
  /* Appearance es el único lugar para decidir color y tema. El wizard conserva
     los valores existentes al editar; los servicios nuevos heredan el tema del
     sitio y siguen el dispositivo de quien reserva. */
  const accent = service?.bookingPage.accent ?? ''
  const theme: Offer['bookingPage']['theme'] = service?.bookingPage.theme ?? 'auto'
  /* Vacío hereda el correo de avisos del proyecto, que es lo normal. */
  const [contactEmail, setContactEmail] = useState(service?.booking.contactEmail ?? '')
  const [contactPhone, setContactPhone] = useState(service?.booking.contactPhone ?? '')
  /* La maqueta necesita un color concreto aunque el servicio herede el suyo de
     Appearance; el valor por defecto evita añadir una segunda lectura de tema
     al wizard. */
  const acentoVisible = accent || DEFAULT_PAGE_THEME.colors.primary
  const [refundPercent, setRefundPercent] = useState(service?.booking.refundPercent ?? 100)
  /* Quién devuelve el dinero al cancelar: el dueño en Stripe (por defecto) o
     véltica sola, con el % de arriba. Ver `services/bookingRefunds.ts`. */
  const [refundAuto, setRefundAuto] = useState(service?.booking.refundAuto ?? false)
  const [refundLate, setRefundLate] = useState(service?.booking.refundLatePercent ?? 0)
  const [latePolicy, setLatePolicy] = useState(service?.booking.latePolicy ?? 'contact')
  const [refillRefund, setRefillRefund] = useState(service?.booking.lateRefillRefund ?? true)
  const [lateCredit, setLateCredit] = useState(service?.booking.lateCredit ?? false)
  const [forgiveFirst, setForgiveFirst] = useState(service?.booking.lateForgiveFirst ?? true)
  const [rules, setRules] = useState<BookingRule[]>(() => initialRules(resource))
  /*
   * Dos densidades para la misma agenda.
   *
   * Una agenda regular abre en «Simple». Si lo guardado ya tiene horas
   * diferentes o turnos partidos, abre directamente el editor completo: no se
   * aplana ni se esconde información sólo por entrar al asistente.
   */
  const [scheduleEditorMode, setScheduleEditorMode] = useState<'simple' | 'advanced'>(
    () => simpleSchedule(initialRules(resource)) ? 'simple' : 'advanced',
  )
  /* Conserva la última franja aunque se cierre el último día. Sin esta memoria,
     al volver a abrir uno saltaría de nuevo a 09:00–17:00. */
  const [simpleRange, setSimpleRange] = useState(() => {
    const schedule = simpleSchedule(initialRules(resource))
    return {
      startMinute: schedule?.startMinute ?? 540,
      endMinute: schedule?.endMinute ?? 1020,
    }
  })
  /*
   * Los días sueltos que no siguen la semana.
   *
   * Ya existían en el modelo y el motor los respetaba, pero no había dónde
   * escribirlos: el asistente los leía del recurso y los volvía a guardar tal
   * cual, así que sólo podían entrar por la base de datos. Ahora se editan
   * aquí, junto al horario del que son la excepción.
   */
  const [exceptions, setExceptions] = useState<BookingException[]>(() => resource?.exceptions ?? [])
  /* Qué hace la fila, dicho con sus propias horas. Es lo que faltaba: sin esto no
     había forma de saber que «abro además» no quita nada. */
  const exceptionHint = (exception: BookingException): string => {
    if (exception.kind === 'closed' && exception.startMinute === null) return t('svcw.exc.closedAll')
    const from = minutesToTime(exception.startMinute ?? 0)
    const to = minutesToTime(exception.endMinute ?? 0)
    if (exception.kind === 'closed') return t('svcw.exc.closedRange', { from, to })
    return t('svcw.exc.openRange', { from, to })
  }

  const patchException = (index: number, patch: Partial<BookingException>) =>
    setExceptions((current) => current.map((item, position) => (position === index ? { ...item, ...patch } : item)))

  /* La nueva nace en MAÑANA y cerrada, que es para lo que se usan nueve de cada
     diez: un día que no trabajas. Mañana y no hoy porque una excepción de hoy no
     cambia casi nada —las horas ya pasaron— y se lee como un error. */
  const addException = () => {
    const day = new Date(Date.now() + 86400000)
    const key = new Intl.DateTimeFormat('en-CA', { timeZone: scheduleZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(day)
    setExceptions((current) => [...current, { day: key, kind: 'closed', startMinute: null, endMinute: null, note: '' }])
  }

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  /* Dónde va la pastilla del raíl. Medido y no calculado, por lo mismo que en
     las pestañas de la ficha: «Información» y «Precio» no miden igual. */
  useLayoutEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const place = () => {
      const active = rail.querySelector<HTMLButtonElement>('button.is-current')
      if (!active) return
      rail.style.setProperty('--step-x', `${active.offsetLeft}px`)
      rail.style.setProperty('--step-w', `${active.offsetWidth}px`)
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [step])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || saving) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previous }
  }, [onClose, saving])

  /**
   * El trozo final de la dirección, sin la barra de delante.
   *
   * `generateSlug` es la de las PÁGINAS y devuelve «/loquesea» —con barra—
   * porque allí el resultado ES la ruta entera. Aquí se pega detrás de
   * «/book/», así que salían dos barras seguidas.
   *
   * Y el respaldo nunca llegaba a usarse: sin título, esa función devuelve «/»,
   * que no es cadena vacía, así que el `||` no saltaba y la dirección terminaba
   * en «/book//». Recortando la barra primero, sin título queda vacío y el
   * respaldo sí entra.
   */
  const slug = useMemo(
    () => generateSlug(title).replace(/^\/+/, '') || t('svcw.page.slugFallback'),
    [title, t],
  )
  const host = siteOrigin ? siteOrigin.replace(/^https?:\/\//, '') : 'tusitio.com'
  const customDurationValid = !customDurationOpen || (Number(customDuration) >= 5 && Number(customDuration) <= 1440)
  const isGroup = capacity > 1
  /**
   * El último tamaño de grupo escrito, para devolverlo al volver a «Grupal».
   *
   * Sin esto, pasar a «Individual» y arrepentirse tiraba el número que la
   * persona acababa de teclear y lo dejaba otra vez en 2.
   */
  const lastGroupSize = useRef(2)
  if (isGroup) lastGroupSize.current = capacity
  const { currency } = useRegion()
  /* El importe ya en la unidad mínima, con el MISMO lector que el resto del
     panel: acepta millares y las dos convenciones decimales. `null` significa
     que lo escrito no es un número, que no es lo mismo que ser cero.

     Se calcula aquí y no arriba porque necesita la moneda de la región, y en
     una moneda sin decimales lo tecleado ya es la unidad entera. */
  const amountMinor = toMinorUnits(amount, currency)
  const paymentValid = paymentChoice === 'free'
    || (included
      ? inclusions !== null && inclusionsValid(inclusions)
      : amountMinor !== null && amountMinor > 0 && (!mixed || (inclusions !== null && inclusionsValid(inclusions))))
  const scheduleValid = rules.length > 0 && rules.every((rule) => rule.startMinute < rule.endMinute)
  /* En la moneda del proyecto y con su formato. Antes iba fijo a pesos: un
     negocio que cobra en dólares veía «$1,200.00 MXN» en la vista previa y el
     servicio se creaba de verdad en pesos. */
  /**
   * Si ya se pasó por el paso del cobro.
   *
   * `step >= 2` y no «el precio tiene un valor»: elegir «se paga en el lugar»
   * es una decisión tan válida como escribir un importe, y con la segunda regla
   * ese caso no se enseñaría nunca. Lo que hay que saber es si alguien lo ha
   * MIRADO, no qué eligió.
   */
  /* Publicar al crear. Apagado por defecto: ver el comentario del bloque. */
  const [publish, setPublish] = useState(service?.status === 'active')
  const [publishBlockDialog, setPublishBlockDialog] = useState<'plan' | 'stripe' | null>(null)
  /* El tope del catálogo, que no es el bloqueo de Stripe: aquél dice «no puedes
     cobrar», éste «no te caben más publicadas». Editando uno ya publicado no
     aplica — ése ya ocupa su hueco. */
  const [limitDialog, setLimitDialog] = useState(false)
  const publishBlockedByPlan = service?.status !== 'active'
    && reachedLimit(publishedOffers, SITE_PLAN_LIMITS[plan].offers)

  /*
   * Si se ha tocado algo desde que se abrió.
   *
   * Se serializa todo lo editable y se compara con una foto tomada al montar.
   * Campo a campo serían once desigualdades que hay que acordarse de ampliar al
   * añadir el doceavo; así el olvido no existe.
   */
  const shape = JSON.stringify({ title, description, shortDescription, images, accent, theme, contactEmail, contactPhone, duration, unit, daysMin, daysMax, capacity, formFields, cancelWindow, moveWindow, reminderEnabled, reminderHours, refundPercent, refundLate, refundAuto, latePolicy, refillRefund, lateCredit, forgiveFirst, paymentChoice, amount, inclusions, spotsDisplay, publish, rules, exceptions, stepMinutes, bufferBefore, bufferAfter, minNotice, maxAdvanceDays, customValues })
  const [baseline] = useState(() => shape)
  const dirty = shape !== baseline
  /**
   * El alta de Stripe, compartida con la lista de servicios.
   *
   * Hace falta AQUÍ y no sólo en el servidor porque de eso depende que se pueda
   * publicar: una reserva que cobra en línea sin cuenta conectada es un botón de
   * pagar que no lleva a ninguna parte, y el servidor la rechaza —ver
   * `assertCanPublish`—. Sin preguntarlo, el mando prometía algo que iba a
   * fallar al pulsar «Crear y publicar».
   */
  const stripe = useStripeConnect(projectId, token, bookingError)

  /**
   * Si publicar está fuera de alcance por falta de cuenta de cobros.
   *
   * Sólo cuando el servicio COBRA EN LÍNEA. Una reserva que se paga en el lugar
   * no pasa por Stripe ni publicada —es la misma excepción de `chargesOnline` en
   * el worker— y exigirle cuenta dejaría sin publicar a quien sólo quiere que le
   * aparten hora, que es el caso más común de una agenda.
   *
   * `stripe.ready === false` y no `!stripe.ready`: mientras no se sabe no se
   * bloquea nada. Con `null` manda el servidor, que es quien decide de verdad.
   */
  const blockedByStripe = paymentMode !== 'onsite' && !included && stripe.ready === false

  /* Y si se apaga la posibilidad con el interruptor ya encendido, se apaga
     también él: dejar encendido un interruptor que no se puede cumplir es
     prometer algo que el botón de crear va a desmentir. */
  useEffect(() => { if (blockedByStripe && publish) setPublish(false) }, [blockedByStripe, publish])

  /*
   * Qué partes de la maqueta se enseñan ya.
   *
   * Creando se van revelando: los días arrancan con un horario de oficina por
   * defecto —lunes a viernes de 9 a 17— así que la ficha enseñaría fechas
   * concretas y reservables desde el primer paso. Serían las de un horario que
   * nadie ha mirado, y son lo más creíble de la página: quien las ve da por
   * hecho que ya eligió cuándo abre.
   *
   * Editando no hay nada que revelar: duración, precio y horario ya están
   * decididos y guardados. Esconderlos hasta llegar a su paso enseñaría una
   * página más pobre que la de verdad, que es lo contrario de una vista previa —
   * y obligaría a recorrer el asistente entero sólo para volver a verla completa.
   */
  const settingsDecided = editing || step >= 1
  const priceDecided = editing || step >= 2
  const priceLabel = paymentChoice === 'free' ? t('svcw.price.freeLabel') : included ? t('svcw.price.membershipLabel') : paymentValid
    ? paymentChoice === 'onsite' ? t('svcw.price.onsiteLabel', { price: formatMoney(amountMinor ?? 0, currency) }) : formatMoney(amountMinor ?? 0, currency)
    : t('svcw.price.pending')
  /*
   * Los huecos de la vista previa, calculados con el MOTOR de verdad.
   *
   * Antes eran una aproximación: la primera regla, tres arranques y a correr —
   * sin márgenes, sin antelación mínima, sin horizonte y sin el paso de arranque.
   * Enseñaba horarios que el servidor luego no ofrece, que es la peor forma de
   * equivocarse en una vista previa: se configura contra una mentira.
   *
   * `computeSlots` es la misma función que responde `/slots` en la página
   * pública, ahora en `@webbuilder/booking` para que las dos la compartan.
   *
   * `busy` va vacío a propósito: un servicio que aún no existe no tiene citas, y
   * editando, enseñar los huecos ya ocupados confundiría lo que se está
   * decidiendo —el horario— con lo que ya pasó.
   */
  /**
   * Un trozo de una fecha `YYYY-MM-DD`, escrito en la zona del negocio.
   *
   * Se arma con mediodía UTC y no con la medianoche del día: a las 00:00 de una
   * fecha, en un huso al oeste todavía es el día anterior, y la tarjeta enseñaría
   * un día distinto del que tiene los huecos.
   */
  const dayLabel = (key: string, part: 'weekday' | 'weekdayLong' | 'day' | 'month' | 'monthLong') => {
    const [y, m, d] = key.split('-').map(Number)
    const at = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1), 12))
    const opts = part === 'weekday' ? { weekday: 'short' as const }
      : part === 'weekdayLong' ? { weekday: 'long' as const }
      : part === 'day' ? { day: 'numeric' as const }
      : part === 'month' ? { month: 'short' as const }
      : { month: 'long' as const }
    const text = new Intl.DateTimeFormat(activeLanguage(), { timeZone: 'UTC', ...opts }).format(at)
    /* Los nombres largos se levantan porque encabezan; los cortos van dentro de
       una tarjeta y ahí la mayúscula grita. */
    return part === 'monthLong' || part === 'weekdayLong'
      ? text.charAt(0).toUpperCase() + text.slice(1)
      : text.replace('.', '')
  }

  /**
   * Un número de minutos, dicho como lo diría una persona.
   *
   * 90 no es «90 min» en una frase: es «hora y media». Las ayudas de abajo lo
   * usan para traducir lo que hay escrito en vez de dar un ejemplo con otro
   * número, que obliga a hacer la cuenta de cabeza.
   */
  const humanMinutes = (raw: string): string => {
    const total = Number(raw) || 0
    if (total === 0) return t('svcw.min.nothing')
    if (total < 60) return t('svcw.min.minutes', { count: total })
    /* A partir de un día entero se dice en días: «72 horas» de antelación mínima
       es correcto y aun así nadie lo cuenta así. */
    if (total >= 1440 && total % 1440 === 0) {
      const days = total / 1440
      return days === 1 ? t('svcw.min.oneDay') : t('svcw.min.days', { count: days })
    }
    const hours = Math.floor(total / 60)
    const rest = total % 60
    const h = hours === 1 ? t('svcw.min.oneHour') : t('svcw.min.hours', { count: hours })
    if (rest === 0) return h
    if (rest === 30) return hours === 1 ? t('svcw.min.oneHourHalf') : t('svcw.min.hoursHalf', { count: hours })
    return t('svcw.min.hoursRest', { hours: h, rest })
  }

  /**
   * Los cinco ajustes finos, con sus topes.
   *
   * Los máximos no son redondos por gusto, salen de para qué sirve cada campo:
   * un margen de doce horas no es un margen, es cerrar el día; una antelación de
   * una semana en MINUTOS son cinco cifras que nadie teclea sin equivocarse.
   */
  const ADVANCED: {
    label: string
    unit: string
    min: number
    max: number
    step: number
    placeholder?: string
    get: () => string
    set: (value: string) => void
    hint: (value: string) => string
  }[] = [
    {
      label: t('svcw.adv.stepLabel'),
      unit: 'min',
      min: 5,
      /* Nunca más que la propia cita: con arranques más separados que su
         duración quedan huecos muertos que no se pueden reservar, y para dejar
         aire está el margen de después, que sí lo cuenta la agenda. */
      max: duration,
      step: 5,
      placeholder: String(duration),
      get: () => stepMinutes,
      set: setStepMinutes,
      hint: (value) => {
        const paso = Number(value) || duration
        if (!value.trim() || paso >= duration) return t('svcw.adv.stepSame', { amount: humanMinutes(String(duration)) })
        const porHora = Math.floor(60 / paso)
        return t('svcw.adv.stepOverlap', {
          amount: humanMinutes(value),
          offer: porHora > 1 ? t('svcw.adv.perHour', { count: porHora }) : t('svcw.adv.moreOptions'),
        })
      },
    },
    {
      label: t('svcw.adv.beforeLabel'),
      unit: 'min',
      min: 0,
      /* Cuatro horas: más que eso no es un margen, es cerrar medio día, y para
         eso está el horario. */
      max: 240,
      step: 5,
      get: () => bufferBefore,
      set: setBufferBefore,
      hint: (value) => {
        if (!(Number(value) > 0)) return t('svcw.adv.beforeNone')
        const base = t('svcw.adv.before', { amount: humanMinutes(value) })
        return busy.length ? base : `${base} ${t('svcw.adv.noBookingsYet')}`
      },
    },
    {
      label: t('svcw.adv.afterLabel'),
      unit: 'min',
      min: 0,
      max: 240,
      step: 5,
      get: () => bufferAfter,
      set: setBufferAfter,
      hint: (value) => {
        if (!(Number(value) > 0)) return t('svcw.adv.afterNone')
        const base = t('svcw.adv.after', { amount: humanMinutes(value) })
        return busy.length ? base : `${base} ${t('svcw.adv.noBookingsYet')}`
      },
    },
    {
      label: t('svcw.adv.noticeLabel'),
      unit: 'min',
      min: 0,
      /* Tres días. Más se pide en días, no en minutos: 10080 son cinco cifras que
         nadie teclea sin equivocarse, y quien necesita más está describiendo un
         horizonte, no un aviso. */
      max: 4320,
      step: 15,
      get: () => minNotice,
      set: setMinNotice,
      hint: (value) => Number(value) > 0
        ? t('svcw.adv.notice', { amount: humanMinutes(value) })
        : t('svcw.adv.noticeNone'),
    },
    {
      label: t('svcw.adv.horizonLabel'),
      unit: t('svcw.adv.horizonUnit'),
      min: 1,
      max: 365,
      step: 1,
      get: () => maxAdvanceDays,
      set: setMaxAdvanceDays,
      hint: (value) => {
        const days = Number(value) || 0
        const meses = days >= 60
          ? t('svcw.adv.aboutMonths', { count: Math.round(days / 30) })
          : days >= 28 ? t('svcw.adv.aboutOneMonth') : ''
        return t('svcw.adv.horizon', {
          days: t(days === 1 ? 'svcw.day.one' : 'svcw.day.many', { count: days }),
          extra: meses,
        })
      },
    },
  ]

  /**
   * Lo que hay puesto, en cuatro palabras.
   *
   * Con los tres apartados cerrados hace falta saber qué hay dentro sin abrirlos
   * —si no, cerrarlos sólo esconde el problema: tres títulos que obligan a abrir
   * los tres para comprobar una cifra—. Esto es lo que se lee en la cabecera.
   */
  const hoursShort = (hours: number) => hours % 24 === 0 && hours >= 24
    ? t(hours / 24 === 1 ? 'svcw.day.one' : 'svcw.day.many', { count: hours / 24 })
    : t('svcw.dur.hours', { count: hours })
  const exceptionsSummary = exceptions.length === 0
    ? t('svcw.sch.none')
    : t(exceptions.length === 1 ? 'svcw.day.one' : 'svcw.day.many', { count: exceptions.length })
  const advancedSummary = (() => {
    const notice = Number(minNotice) || 0
    const days = Math.max(1, Number(maxAdvanceDays) || 30)
    const partes = [
      notice > 0 ? t('svcw.sch.noticeOf', { amount: humanMinutes(minNotice) }) : t('svcw.sch.noNotice'),
      t(days === 1 ? 'svcw.day.one' : 'svcw.day.many', { count: days }),
    ]
    /* Los márgenes se nombran pero no se detallan: son dos cifras más y aquí
       sobra saber si los hay. El número está a un clic. */
    if ((Number(bufferBefore) || 0) > 0 || (Number(bufferAfter) || 0) > 0) partes.push(t('svcw.sch.withBuffers'))
    return partes.join(' · ')
  })()
  const whenSummary = (() => {
    const cancel = Math.max(0, Number(cancelWindow) || 0)
    const move = moveWindow.trim() ? Math.max(0, Number(moveWindow)) : cancel
    if (cancel === 0 && move === 0) return t('svcw.cancel.noLimit')
    if (cancel === move) return t('svcw.cancel.beforeAmount', { amount: hoursShort(cancel) })
    return t('svcw.cancel.cancelMove', { cancel: hoursShort(cancel), move: hoursShort(move) })
  })()
  const refundSummary = paymentMode === 'onsite' || included
    ? t('svcw.cancel.notApplicable')
    : t('svcw.cancel.refundSummary', { inTime: refundPercent, late: refundLate }) + (refundAuto ? ` · ${t('svcw.cancel.refundAutoShort')}` : '')
  const lateSummary = (() => {
    const label = t(latePolicy === 'contact' ? 'svcw.cancel.manual' : latePolicy === 'move' ? 'svcw.cancel.onlyMove' : 'svcw.cancel.noChanges')
    const extras = [refillRefund, lateCredit, forgiveFirst].filter(Boolean).length
    return extras
      ? t('svcw.cancel.summaryWith', {
          label,
          extras: t(extras === 1 ? 'svcw.cancel.conditionOne' : 'svcw.cancel.conditionMany', { count: extras }),
        })
      : label
  })()

  /** Qué significa el plazo puesto, con sus propias palabras. */
  const windowHint = (raw: string, verbo: string): string => {
    const hours = Number(raw) || 0
    const action = t(verbo === 'cancel' ? 'svcw.cancel.theCancel' : 'svcw.cancel.theMove')
    if (hours <= 0) return t('svcw.cancel.untilStart', { action })
    const dicho = hours === 1
      ? t('svcw.min.oneHour')
      : hours % 24 === 0
        ? (hours === 24 ? t('svcw.min.oneDay') : t('svcw.min.days', { count: hours / 24 }))
        : t('svcw.min.hours', { count: hours })
    return t('svcw.cancel.closesBefore', { action, amount: dicho })
  }

  const refundHint = (value: number): string => value >= 100
    ? t('svcw.cancel.refundFull')
    : value <= 0
      ? t('svcw.cancel.refundNone')
      : t('svcw.cancel.refundPartial', { percent: value, kept: 100 - value })

  /** El valor dentro de su rango. Vacío se respeta: es «usa el de por defecto». */
  const clampField = (value: string, field: { min: number; max: number }): string => {
    if (!value.trim()) return ''
    return String(Math.min(field.max, Math.max(field.min, Math.round(Number(value)) || field.min)))
  }

  /**
   * Las citas que ocupan sitio. Las canceladas y las no-presentadas no: ese
   * hueco volvió al mercado.
   */
  const busy = useMemo(
    () => bookings
      .filter((booking) => booking.status === 'held' || booking.status === 'confirmed' || booking.status === 'completed')
      .map((booking) => ({ startsAt: Date.parse(booking.startsAt), endsAt: Date.parse(booking.endsAt), seats: 1 }))
      .filter((interval) => Number.isFinite(interval.startsAt) && Number.isFinite(interval.endsAt)),
    [bookings],
  )

  /**
   * Desde hoy hasta donde llega la vista previa.
   *
   * Izado porque lo necesitan dos: los horarios libres y las citas vendidas. Si
   * cada uno calculaba el suyo, bastaba con que discreparan un día para que
   * apareciera una cita en una fecha que la tira no enseña.
   */
  const range = useMemo(() => {
    const zone = resource?.timezone ?? timezone
    const from = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
    const horizon = Math.min(60, Math.max(1, Number(maxAdvanceDays) || 30))
    const [y, m, d] = from.split('-').map(Number)
    const to = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + horizon)).toISOString().slice(0, 10)
    return { from, to, horizon }
  }, [resource, timezone, maxAdvanceDays])

  const availability = useMemo(() => {
    if (!scheduleValid) return []
    const zone = resource?.timezone ?? timezone
    const { from: today, to, horizon } = range
    try {
      if (unit === 'day') {
        return computeDaySlots({
          timezone: zone,
          rules,
          exceptions,
          busy,
          days: daysMin,
          capacity,
          minNoticeMinutes: Number(minNotice) || 0,
          maxAdvanceDays: horizon,
          now: Date.now(),
          fromDay: today,
          toDay: to,
        })
      }
      return computeSlots({
        timezone: zone,
        rules,
        exceptions,
        busy,
        durationMinutes: duration,
        bufferBeforeMinutes: Number(bufferBefore) || 0,
        bufferAfterMinutes: Number(bufferAfter) || 0,
        ...(stepMinutes.trim() ? { stepMinutes: Number(stepMinutes) } : {}),
        capacity,
        minNoticeMinutes: Number(minNotice) || 0,
        maxAdvanceDays: horizon,
        now: Date.now(),
        fromDay: today,
        toDay: to,
      })
    } catch {
      /* Un ajuste a medio escribir puede dar una combinación imposible. La
         maqueta se queda sin horarios, que es lo mismo que dice el motor. */
      return []
    }
  }, [scheduleValid, range, resource, timezone, rules, exceptions, busy, duration, bufferBefore, bufferAfter, stepMinutes, capacity, minNotice, unit, daysMin])

  /** Los días con hueco, en la zona del negocio, en orden. */
  const availableDays = useMemo(() => {
    const zone = resource?.timezone ?? timezone
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' })
    const hhmm = new Intl.DateTimeFormat('es-MX', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    const map = new Map<string, { times: string[]; taken: TakenSlot[] }>()
    const bucket = (key: string) => {
      const found = map.get(key)
      if (found) return found
      const fresh = { times: [] as string[], taken: [] as TakenSlot[] }
      map.set(key, fresh)
      return fresh
    }
    for (const slot of availability) bucket(day.format(new Date(slot.startsAt))).times.push(hhmm.format(new Date(slot.startsAt)))
    /*
     * Y las citas que YA están vendidas, en la misma fila que los huecos.
     *
     * El motor las quita de `availability` —para el cliente ese hueco no existe—
     * así que sin esto la hora de las once desaparecía sin más y no había manera
     * de saber si la había comido una reserva, un margen o el horario. Aquí sí
     * hace falta verlas: es lo que se viene a comprobar.
     */
    for (const booking of scheduleValid ? bookings : []) {
      if (booking.status !== 'held' && booking.status !== 'confirmed' && booking.status !== 'completed') continue
      const start = new Date(booking.startsAt)
      const end = new Date(booking.endsAt)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue
      const key = day.format(start)
      /* Fuera del horizonte no se enseñan: la tira sólo llega hasta donde llega
         la vista previa, y una cita más allá crearía un día suelto al final. */
      if (key < range.from || key > range.to) continue
      bucket(key).taken.push({
        id: booking.id,
        time: hhmm.format(start),
        endTime: hhmm.format(end),
        name: booking.customerName.trim() || translate('common.unnamed'),
        email: booking.customerEmail.trim(),
        phone: booking.customerPhone.trim(),
        status: booking.status,
      })
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, value]) => ({
        key,
        times: value.times,
        taken: value.taken.sort((a, b) => a.time.localeCompare(b.time)),
      }))
  }, [availability, bookings, range, resource, scheduleValid, timezone])

  /* El día que se está mirando en la maqueta. Vacío = el primero con hueco. */
  const [previewDay, setPreviewDay] = useState('')
  const shownDay = availableDays.find((entry) => entry.key === previewDay) ?? availableDays[0] ?? null

  /* Qué cita se está mirando y DÓNDE está su hora. Se guarda el id y se BUSCA en
     el día que se enseña, no se guarda la cita: así, al cambiar de día, el banner
     se cierra solo en vez de quedarse enseñando a alguien que no viene hoy. */
  const [peek, setPeek] = useState<{ id: string; x: number; arrow: number; top: number; bottom: number; below: boolean } | null>(null)

  /* El banner se ancla a la pantalla, así que deja de valer en cuanto la hora se
     mueve. Se cierra al desplazar o redimensionar en vez de recolocarse: quien
     hace rueda no está mirando el banner, está buscando otra hora. */
  useEffect(() => {
    if (!peek) return
    const close = () => setPeek(null)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [peek])
  const peeked = shownDay?.taken.find((entry) => entry.id === peek?.id) ?? null

  /**
   * Coloca el banner sobre la hora señalada.
   *
   * Se mide en vez de dejarlo al CSS por dos cosas que el CSS solo no sabe. Una:
   * el banner es mucho más ancho que una hora, así que centrado sobre una de los
   * extremos se saldría del cuerpo —que recorta—; se limita a quedarse dentro, y
   * la flecha se queda apuntando a la hora aunque el banner ya no esté centrado
   * sobre ella. Dos: sobre la primera fila no cabe encima, así que ahí baja.
   */
  const openPeek = (id: string, chip: HTMLElement) => {
    const box = chip.getBoundingClientRect()
    const center = box.left + box.width / 2
    const half = Math.min(PEEK_WIDTH, window.innerWidth - 16) / 2
    /* Arriba si cabe. Se mide contra la ventana y no contra el cuerpo del
       probador porque ya no se dibuja dentro de él. */
    const below = box.top < PEEK_HEIGHT + 12
    setPeek({
      id,
      /* El banner, dentro de la PANTALLA. Antes se metía dentro de la rejilla de
         horas, que mide poco más de lo que mide él: con tan poco margen sólo le
         cabía una posición, el centro, así que nunca quedaba sobre su hora. */
      x: Math.max(half + 8, Math.min(window.innerWidth - half - 8, center)),
      /*
       * La flecha, en el centro de la hora y SIN esa limitación.
       *
       * Compartían el mismo número y por eso señalaba a otra hora: cuando el
       * banner es tan ancho como la rejilla —que es lo normal en este panel—, la
       * limitación deja un solo sitio posible, el centro exacto de la rejilla. El
       * banner tiene que ir ahí, no le queda otro, pero la flecha no: su trabajo
       * es decir de qué hora habla, y ahí acababa apuntando siempre al medio.
       */
      arrow: center,
      top: box.top,
      bottom: box.bottom,
      below,
    })
  }
  const closePeek = (id: string) => setPeek((current) => (current?.id === id ? null : current))

  /**
   * A qué distancia está una página de cinco días, medida.
   *
   * `clientWidth` NO sirve como zancada, que es lo que hacían las flechas: una
   * página son cinco tarjetas MÁS los cinco huecos entre ellas, y el ancho
   * visible sólo contiene cuatro huecos. Sobran ocho píxeles por página, que se
   * acumulan; a la tercera flecha la tira quedaba a media tarjeta.
   */
  const stripPage = (strip: HTMLDivElement): number => {
    const card = strip.querySelector<HTMLButtonElement>('[data-day]')
    if (!card) return strip.clientWidth
    const pitch = card.offsetWidth + 8
    return Math.max(1, Math.round(strip.clientWidth / pitch)) * pitch
  }

  /*
   * La tira se mueve hasta el día elegido, y aterriza en el principio de su
   * página de cinco.
   *
   * Sin esto, elegir «Septiembre» en el desplegable cambiaba el día por dentro
   * —y las horas de abajo— pero la tira seguía enseñando agosto: parecía que el
   * mando no hacía nada.
   *
   * Y aterriza donde aterrizan las flechas a propósito. Antes se dejaba caer en
   * `offsetLeft - 8`, ocho píxeles a la izquierda de cualquier posición válida,
   * así que el ajuste obligatorio del navegador tiraba de la tira hacia la
   * tarjeta más cercana en cuanto soltábamos: elegías un día y la tira acababa en
   * otro sitio. Ahora el destino ya es una posición de tarjeta.
   */
  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip || !shownDay) return
    const card = strip.querySelector<HTMLButtonElement>(`[data-day="${shownDay.key}"]`)
    if (!card) return
    /*
     * Medido contra la tira, no con `offsetLeft`.
     *
     * `offsetLeft` cuenta desde el ancestro POSICIONADO más cercano, y la tira no
     * lo está: el cero caía más arriba, así que a cada distancia se le sumaba la
     * flecha de la izquierda y el relleno de la tarjeta. Con ese sobrante, la
     * cuenta de la página se pasaba de largo y elegir un día visible movía la
     * tira una columna a la derecha. Los rectángulos no dependen de eso.
     */
    const left = card.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft
    if (left >= strip.scrollLeft && left + card.offsetWidth <= strip.scrollLeft + strip.clientWidth) return
    const page = stripPage(strip)
    strip.scrollTo({ left: Math.max(0, Math.floor(left / page) * page), behavior: 'smooth' })
  }, [shownDay])

  /*
   * ── El encuadre de la vista previa, MEDIDO ────────────────────────────────
   *
   * Estuvo escrito como porcentajes a mano —«la línea de datos está al 52%»— y
   * eso no se sostiene: la ficha cambia de alto según haya imagen o tres
   * líneas de descripción, así que cualquier número acertado hoy deja de estarlo
   * en cuanto alguien escribe. Se notaba en que las zonas de abajo salían
   * pegadas al techo del marco.
   *
   * Ahora se mide la zona y se centra. `offsetTop` y compañía son medidas de
   * MAQUETACIÓN: no las afecta el `transform` que ya tenga puesto, así que se
   * puede calcular el encuadre nuevo sin tocar el actual —y sin eso, medir
   * obligaría a quitar la animación a mitad de camino.
   *
   * La cuenta: para que el centro de la zona caiga en el centro del marco, el
   * desplazamiento es `mitad-del-marco / escala − centro-de-la-zona`, todo en
   * fracciones de la propia ficha.
   */
  const viewportRef = useRef<HTMLDivElement>(null)
  const wizardRef = useRef<HTMLDivElement>(null)
  useScrollTopOnStep(step, wizardRef)
  const scaleRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const stage = scaleRef.current
    if (!viewport || !stage) return

    const page = stage.firstElementChild as HTMLElement | null
    if (!page) return

    if (zoom === 'full') {
      stage.style.transform = 'none'
      return
    }

    const target = page.querySelector<HTMLElement>(`[data-zone="${zoom}"]`)
    if (!target) { stage.style.transform = 'none'; return }

    const scale = ZONE_SCALE[zoom]
    /* Del borde de la zona al borde de la ficha, sumando la cadena de
       posicionados. `.dash-svc-page` es `position: relative`, así que la cadena
       termina en ella. */
    let left = 0
    let top = 0
    for (let node: HTMLElement | null = target; node && node !== page; node = node.offsetParent as HTMLElement | null) {
      left += node.offsetLeft
      top += node.offsetTop
    }

    /*
     * Centrar la zona, pero SIN despegar la ficha del marco.
     *
     * Es el acotado de un mapa: se puede arrastrar hasta donde el borde de la
     * imagen toca el borde del marco, y ni un píxel más. Antes sólo se acotaba
     * por arriba y por la izquierda, y por eso las zonas del pie —la línea de
     * datos, el precio— salían centradas pero con una franja vacía debajo, que
     * es lo que se leía como «está muy arriba».
     *
     * El mínimo es dónde queda el desplazamiento cuando el borde derecho (o el
     * de abajo) de la ficha ya coincide con el del marco. Si la ficha escalada
     * cabe entera, ese mínimo es positivo y el acotado la deja pegada al
     * principio, que es lo correcto: no hay nada que recorrer.
     */
    const fit = (start: number, extent: number, frame: number, size: number) => {
      const min = frame / (scale * size) - 1
      /* Cuánto de la ficha cabe en el marco a esta escala. */
      const visible = frame / (scale * size)
      /*
       * Una zona MÁS ALTA que lo que se ve no se centra: se apoya al principio.
       *
       * Centrar algo que no cabe recorta por los dos lados a la vez, y entonces
       * no se ve ni su principio ni su final — que es lo que pasaba con el
       * bloque de información entero: la imagen de portada quedaba por encima
       * del borde de arriba. Apoyada al principio se lee como un documento, de
       * arriba abajo.
       */
      const wanted = extent >= visible
        ? -start
        : (0.5 * frame) / (scale * size) - (start + extent / 2)
      return Math.min(0, Math.max(min, wanted))
    }

    const tx = fit(left / page.offsetWidth, target.offsetWidth / page.offsetWidth, viewport.clientWidth, page.offsetWidth)
    const ty = fit(top / page.offsetHeight, target.offsetHeight / page.offsetHeight, viewport.clientHeight, page.offsetHeight)
    stage.style.transform = `scale(${scale}) translate(${(tx * 100).toFixed(2)}%, ${(ty * 100).toFixed(2)}%)`
    /* Se recalcula también cuando cambia lo que mide la ficha: al escribir una
       descripción larga, al poner una imagen o al abrir días en el horario. */
  }, [zoom, title, description, shortDescription, images, duration, capacity, priceLabel, settingsDecided, priceDecided, availableDays, formFields, rules])

  /*
   * Al llegar al resumen se suelta el foco del botón que trajo hasta aquí.
   *
   * Es la otra mitad del salto: aunque crear ya no responda a Intro, el botón
   * que decía «Siguiente» sigue enfocado cuando pasa a decir «Crear servicio»,
   * y un Intro de más —o el segundo clic de quien va rápido— caía sobre una
   * acción que no había leído. Soltando el foco, el siguiente Intro no le llega
   * a nadie.
   *
   * Va al panel del paso y no a la nada, para que quien navega con teclado
   * siga teniendo dónde estar: desde ahí, un tabulador lleva al pie.
   */
  const stepPanelRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (step !== STEPS.length - 1) return
    const active = document.activeElement as HTMLElement | null
    active?.blur()
    stepPanelRef.current?.focus()
  }, [step])

  /**
   * Qué campo está mal y por qué, en el propio campo.
   *
   * El motivo salía al pie de la columna, lejos del sitio donde había que
   * hacer algo: en un paso largo, quien pulsa «Siguiente» ni siquiera lo ve —le
   * queda fuera de la pantalla— y lo único que percibe es que el botón no
   * funciona. El mensaje tiene que estar donde está el problema.
   *
   * `null` mientras no falla nada. El campo se marca con `aria-invalid` y su
   * texto se enlaza con `aria-describedby`, que es lo que hace que un lector de
   * pantalla lo lea al llegar en vez de dejarlo mudo.
   */
  const [invalid, setInvalid] = useState<{ field: string; message: string } | null>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const priceRef = useRef<HTMLInputElement>(null)
  const durationRef = useRef<HTMLInputElement>(null)

  /* Al corregir, el aviso se va solo: mantenerlo mientras se escribe lo
     convierte en un reproche que ya no aplica. */
  useEffect(() => { if (invalid?.field === 'title' && title.trim()) setInvalid(null) }, [title, invalid])
  useEffect(() => { if (invalid?.field === 'amount' && paymentValid) setInvalid(null) }, [paymentValid, invalid])
  useEffect(() => { if (invalid?.field === 'duration' && customDurationValid) setInvalid(null) }, [customDurationValid, invalid])
  useEffect(() => { if (invalid?.field === 'schedule' && scheduleValid) setInvalid(null) }, [scheduleValid, invalid])

  const fail = (field: string, message: string, focus?: HTMLElement | null) => {
    setInvalid({ field, message })
    /* Llevar el cursor a lo que falta ahorra el viaje de buscarlo, y en un paso
       que no cabe en pantalla es la diferencia entre corregir y rendirse. */
    focus?.focus()
    return false
  }

  useEffect(() => { if (preguntasAMedias === 0) setSeñalarPreguntas(false) }, [preguntasAMedias])

  const validateStep = (target = step) => {
    setError('')
    setInvalid(null)
    if (target === 0 && !title.trim()) return fail('title', t('svcw.invalid.title'), titleRef.current)
    if (target === 1 && !customDurationValid) return fail('duration', t('svcw.invalid.duration'), durationRef.current)
    if (target === 2 && !paymentValid) return fail('amount', t(included ? 'svcw.invalid.membership' : 'svcw.invalid.amount'), priceRef.current)
    /*
     * Aquí no hay mensaje: lo dice el propio campo, en rojo.
     *
     * Un renglón al pie diciendo «una de las preguntas no tiene texto» obliga a
     * leerlo, entenderlo y volver a buscar cuál es, cuando el campo vacío está
     * a la vista y puede decirlo él. Se marcan todas las que falten y el paso no
     * avanza; eso es la frase entera, sin frase.
     */
    if (target === 3 && preguntasAMedias > 0) {
      setSeñalarPreguntas(true)
      return false
    }
    if (target === 4 && !scheduleValid) return fail('schedule', t('svcw.invalid.schedule'))
    /* El correo de contacto, si se escribió uno: acaba impreso en la página de
       quien reserva, como la dirección a la que escribir. Uno mal escrito ahí no
       falla en ninguna parte —simplemente nadie contesta—, así que se comprueba
       antes de guardarlo. Vacío es válido: hereda el del proyecto. */
    if (target === 5 && contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
      return fail('contactEmail', t('bookings.svc.detail.emailInvalid'))
    }
    return true
  }
  const next = () => { if (validateStep()) setStep((current) => Math.min(current + 1, STEPS.length - 1)) }

  /**
   * Enviar el formulario avanza, y sólo avanza.
   *
   * Antes también CREABA cuando ya estabas en el resumen, y ahí es donde el
   * asistente se saltaba solo el último paso: se avanza con Intro o pulsando
   * «Siguiente», y ese mismo botón —que sigue enfocado y bajo el cursor— pasa a
   * ser «Crear servicio» en cuanto cambia el paso. Un segundo Intro, o el
   * segundo clic de quien va rápido, creaba el servicio antes de que nadie
   * llegara a leer el resumen.
   *
   * Crear es ahora un gesto aparte: el botón del pie llama a `create` a mano.
   * Así ningún Intro heredado del paso anterior puede crear nada, que es la
   * única forma de que el resumen se pueda leer.
   */
  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (step < STEPS.length - 1) next()
  }

  const create = async () => {
    if (saving) return
    /*
     * Si algo falta, se va al paso donde falta.
     *
     * Guardando desde cualquier sitio —que es lo que se puede hacer editando—
     * el fallo puede estar en un paso que no está en pantalla: el mensaje se
     * pintaría en un panel oculto y el foco iría a un campo que no existe, así
     * que pulsar «Guardar» no haría absolutamente nada.
     *
     * Se revalida DESPUÉS de mover el paso, en el fotograma siguiente: hasta que
     * React no lo pinta, el campo al que hay que llevar el cursor todavía no
     * está en el DOM.
     */
    const firstBad = [0, 1, 2, 3, 4].find((index) => !validateStep(index))
    if (firstBad !== undefined) {
      setStep(firstBad)
      requestAnimationFrame(() => { validateStep(firstBad) })
      return
    }
    setSaving(true); setError('')
    try {
      if (resource) {
        /* El de la persona en pantalla y los de las demás que se tocaron. */
        const pending = { ...editedSchedules.current, [resource.id]: { rules, exceptions } }
        let latest: BookingResource[] | null = null
        for (const [resourceId, schedule] of Object.entries(pending)) {
          if (!teamIds.includes(resourceId) && resourceId !== resource.id) continue
          latest = (await saveBookingSchedule(projectId, resourceId, schedule, token)).resources
        }
        if (latest) onResourcesChange?.(latest)
      }
      const base = { title: title.trim(), description: description.trim(), shortDescription: shortDescription.trim(), images, type: 'service' as const }
      let saved = service
        /* Recorrido entero (la validación de arriba lo exige): deja de estar
           «por completar» y ya se puede activar. */
        ? await updateOffer(projectId, service.id, { ...base, ...(completing ? { setupPending: false as const } : {}) }, token)
        : await createOffer(projectId, base, token)

      if (paymentChoice !== 'free' && !included) {
        /* Ya validado en el paso del cobro: aquí no puede ser nulo. El cero es
           un cinturón, no un caso real. */
        const wanted = amountMinor ?? 0
        const current = saved.prices.find((row) => row.active && row.isDefault) ?? saved.prices.find((row) => row.active)
        if (!current || current.unitAmount !== wanted) {
          /*
           * Primero se añade el nuevo y después se retira el viejo, y ese orden
           * importa: el servidor se niega a retirar el ÚNICO precio activo de una
           * oferta publicada. Al revés, editar el precio de un servicio a la
           * venta fallaba a mitad y lo dejaba sin ninguno.
           */
          saved = await addOfferPrice(
            projectId,
            saved.id,
            // La moneda la decide el servidor con el ajuste de región; va aquí
            // sólo para que el panel y lo guardado digan lo mismo sin recargar.
            { unitAmount: wanted, currency, interval: 'one_time', isDefault: true },
            token,
          )
          if (current) saved = await removeOfferPrice(projectId, saved.id, current.id, token)
        }
      }

      saved = await updateOffer(projectId, saved.id, {
        customValues,
        bookingDurationMinutes: unit === 'day' ? daysMin * 1440 : duration, bookingUnit: unit, bookingDaysMin: daysMin, bookingDaysMax: unit === 'day' ? Math.max(daysMin, daysMax) : daysMin, bookingCapacity: capacity, bookingPaymentMode: paymentMode,
        /* El primero sigue siendo «su recurso» para quien lea sólo ése; la lista
           manda cuando hay varios. */
        bookingResourceId: teamIds[0] ?? resource?.id ?? null,
        bookingResourceIds: activeTeam.length > 1 ? teamIds : [],
        bookingAssignment: assignment,
        bookingSpotsDisplay: spotsDisplay,
        bookingMaxParty: capacity > 1 ? Math.max(1, Math.min(capacity, maxParty)) : 1,
        /* Siempre por lugar, como un boleto: el precio fijo de un grupo privado
           es un servicio individual, no un ajuste aparte. */
        bookingPricePer: 'person',
        /*
         * Vacío es «como la duración de la cita», que el servidor entiende como
         * nulo. Los demás caen a cero, que es «sin margen».
         *
         * Y se acotan otra vez aquí, no sólo al salir del campo: el tope del
         * arranque es la duración, así que subir el paso a 60 y BAJAR después la
         * duración a 30 deja un valor que ya no vale sin que nadie vuelva a tocar
         * ese campo.
         */
        bookingStepMinutes: unit === 'time' && stepMinutes.trim() ? Math.min(duration, Math.max(5, Number(stepMinutes))) : null,
        bookingBufferBeforeMinutes: Math.min(240, Math.max(0, Number(bufferBefore) || 0)),
        bookingBufferAfterMinutes: Math.min(240, Math.max(0, Number(bufferAfter) || 0)),
        bookingMinNoticeMinutes: Math.min(4320, Math.max(0, Number(minNotice) || 0)),
        bookingMaxAdvanceDays: Math.min(365, Math.max(1, Number(maxAdvanceDays) || 30)),
        bookingPageAccent: accent || null,
        bookingPageTheme: theme,
        /* Recortados: acaban impresos en la página pública, y un correo con
           espacios delante no es el mismo correo. Vacío es válido —hereda el
           del proyecto—. */
        bookingContactEmail: contactEmail.trim(),
        bookingContactPhone: contactPhone.trim(),
        bookingCancelWindowHours: Math.min(720, Math.max(0, Number(cancelWindow) || 0)),
        bookingReminderEnabled: reminderEnabled,
        bookingReminderHours: Math.min(168, Math.max(1, Number(reminderHours) || 24)),
        bookingRescheduleWindowHours: moveWindow.trim() ? Math.min(720, Math.max(0, Number(moveWindow))) : null,
        bookingRefundPercent: refundPercent,
        bookingRefundAuto: paymentMode !== 'onsite' && refundAuto,
        bookingRefundLatePercent: refundLate,
        bookingLatePolicy: latePolicy,
        bookingLateRefillRefund: refillRefund,
        bookingLateCredit: lateCredit,
        bookingLateForgiveFirst: forgiveFirst,
        /* El editor ya sólo entrega las completas; el filtro se queda como
           cinturón y usa la MISMA regla, no una copia que se desincronice. */
        bookingFormFields: formFields.filter(isCompleteFormField),
      }, token)
      /* Gratis: sin precio. Se retira DESPUÉS de pasar a «sin cobro en línea»:
         antes, el servidor no deja quitar el único precio de un servicio a la venta. */
      if (paymentChoice === 'free' || included) {
        for (const row of saved.prices.filter((price) => price.active)) {
          saved = await removeOfferPrice(projectId, saved.id, row.id, token)
        }
      }
      /* Incluida con membresía (sólo miembros o mixto): qué membresías y
         paquetes, y lo que da cada uno. Sin la casilla, ninguna. */
      if (!included && !mixed && hadInclusions) {
        await saveBookingInclusions(projectId, saved.id, [], token).catch(() => undefined)
      } else if ((included || mixed) && inclusions) {
        await saveBookingInclusions(projectId, saved.id, inclusions.filter((draft) => draft.selected).map((draft) => ({
          offerId: draft.offerId,
          bookingsPerPeriod: draft.kind === 'membership' ? draft.bookingsPerPeriod : null,
          packCredits: draft.kind === 'pack' ? draft.packCredits : null,
          packValidDays: draft.kind === 'pack' ? draft.packValidDays : null,
        })), token)
      }
      /*
       * Publicar es un paso APARTE, y a propósito.
       *
       * El servidor puede negarse —una reserva que cobra en línea necesita
       * Stripe conectado, ver `assertCanPublish`— y si eso tumbara la creación
       * entera, quien no tiene Stripe perdería todo lo que acaba de rellenar por
       * haber tocado un interruptor. Así el servicio queda guardado en borrador y
       * lo único que falla es publicarlo, con su motivo delante.
       */
      /* Sólo si CAMBIA. Editando un servicio ya publicado, repetir la petición
         sería pedirle al servidor que vuelva a autorizar algo que ya autorizó, y
         un fallo suyo de red rompería una edición que no tocaba el estado. */
      const wantedStatus = publish ? 'active' : 'draft'
      if (saved.status !== wantedStatus) {
        try {
          saved = await updateOffer(projectId, saved.id, { status: wantedStatus }, token)
        } catch (publishError) {
          setError(bookingError(publishError))
          setSaving(false)
          /* Se entrega igual: existe, y perderlo por no poder publicarlo sería
             castigar al cliente por una condición que no controla. */
          onCreated(saved)
          return
        }
      }
      onCreated(saved)
    } catch (submitError) { setError(bookingError(submitError)); setSaving(false) }
  }

  const toggleDay = (weekday: number, open: boolean) => setRules((current) => open
    ? [...current, { weekday, startMinute: 540, endMinute: 1020 }]
    : current.filter((rule) => rule.weekday !== weekday))
  const patchRule = (index: number, field: 'startMinute' | 'endMinute', value: string) => {
    const minute = timeToMinutes(value)
    if (minute !== null) setRules((current) => current.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, [field]: minute } : rule))
  }
  const compactSchedule = simpleSchedule(rules)
  const compactRange = rules.length && compactSchedule
    ? { startMinute: compactSchedule.startMinute, endMinute: compactSchedule.endMinute }
    : simpleRange
  /* Pasar a editar el horario de otra persona del equipo: lo de la actual se
     recuerda, y lo de la otra sale de lo recordado o de lo guardado. */
  const editScheduleOf = (resourceId: string) => {
    if (resourceId === scheduleOwnerId) return
    if (resource) editedSchedules.current[resource.id] = { rules, exceptions }
    const target = resources.find((row) => row.id === resourceId)
    if (!target) return
    const stored = editedSchedules.current[resourceId]
    const nextRules = stored?.rules ?? initialRules(target)
    setRules(nextRules)
    setExceptions(stored?.exceptions ?? target.exceptions)
    const compact = simpleSchedule(nextRules)
    setScheduleEditorMode(compact ? 'simple' : 'advanced')
    if (compact) setSimpleRange({ startMinute: compact.startMinute, endMinute: compact.endMinute })
    setScheduleOwnerId(resourceId)
  }
  const toggleTeamMember = (resourceId: string) => {
    const next = teamIds.includes(resourceId) ? teamIds.filter((id) => id !== resourceId) : [...teamIds, resourceId]
    /* Siempre alguien: un servicio sin nadie que lo dé no se puede reservar. */
    if (!next.length) return
    const ordered = activeTeam.map((row) => row.id).filter((id) => next.includes(id))
    setTeamIds(ordered)
    if (!ordered.includes(scheduleOwnerId) && ordered[0]) editScheduleOf(ordered[0])
  }
  /* Agregar a alguien al equipo sin salir del asistente. Nace con la semana
     que se está editando —casi siempre el equipo abre las mismas horas— y ya
     marcado para este servicio. */
  const [addingMember, setAddingMember] = useState(false)
  const [memberName, setMemberName] = useState('')
  const [memberRole, setMemberRole] = useState('')
  const [memberBusy, setMemberBusy] = useState(false)
  const [memberError, setMemberError] = useState('')
  const addMember = async () => {
    const name = memberName.trim()
    if (!name || memberBusy) return
    setMemberBusy(true); setMemberError('')
    try {
      const created = await createBookingResource(projectId, { name, role: memberRole.trim() }, token)
      const { resources: latest } = await saveBookingSchedule(projectId, created.id, { rules, exceptions: [] }, token)
      onResourcesChange?.(latest)
      setTeamIds((current) => [...current, created.id])
      setMemberName(''); setMemberRole(''); setAddingMember(false)
    } catch (cause) {
      setMemberError(bookingError(cause))
    } finally {
      setMemberBusy(false)
    }
  }
  const chooseScheduleEditor = (mode: 'simple' | 'advanced') => {
    if (mode === 'simple' && compactSchedule && rules.length) {
      setSimpleRange({ startMinute: compactSchedule.startMinute, endMinute: compactSchedule.endMinute })
    }
    setScheduleEditorMode(mode)
  }
  const toggleSimpleDay = (weekday: number) => {
    if (!compactSchedule) return
    const open = compactSchedule.weekdays.includes(weekday)
    setRules((current) => open
      ? current.filter((rule) => rule.weekday !== weekday)
      : [...current, { weekday, startMinute: compactRange.startMinute, endMinute: compactRange.endMinute }])
  }
  const patchSimpleRange = (field: 'startMinute' | 'endMinute', value: string) => {
    const minute = timeToMinutes(value)
    if (minute === null) return
    const next = { ...compactRange, [field]: minute }
    setSimpleRange(next)
    setRules((current) => current.map((rule) => ({ ...rule, [field]: minute })))
  }

  return createPortal(
    <div ref={wizardRef} className="dash-svc-wizard" role="dialog" aria-modal="true" aria-label={editing ? t('svcw.editAria', { name: service?.title ?? '' }) : t('svcw.newTitle')}>
      {/* La misma barra que «Página nueva», reducida a lo que no dice ya el
          raíl: marca, menú de área y la salida. El título de la pantalla y el
          «Paso 1 de 7 · Datos» estaban a los lados diciendo lo que el raíl ya
          enseña, y le comían el ancho justo a él. El nombre del servicio se
          pide en su paso, encima de la descripción. */}
      <WizardTopBar
        onBack={onClose}
        center={(
        <nav className="dash-svc-wizard-progress" aria-label={t('svcw.progressAria')}>
          <div className="dash-svc-wizard-rail" ref={railRef}>
            {/* La misma pastilla medida que las pestañas de la ficha: seis
                etiquetas de anchos distintos, así que su sitio se mide y no se
                reparte. */}
            <span className="dash-svc-wizard-rail-thumb" aria-hidden="true" />
          {/*
            Editando se puede saltar a cualquier paso; creando, sólo hacia atrás.

            En un alta, el paso de más adelante todavía no tiene sentido: sus
            campos dependen de decisiones que aún no se han tomado —el precio
            depende del modo de cobro, el resumen de todo lo anterior— y dejar
            entrar ahí enseña una pantalla a medias.
            Editando ya existe todo, así que el orden deja de ser una secuencia y
            pasa a ser un índice: se entra a arreglar una cosa concreta.
          */}
          {STEPS.map(({ key, Icon }, index) => {
            const reachable = (editing && !completing) || index < step
            return (
              <button
                type="button"
                key={key}
                className={`${index === step ? 'is-current' : ''}${index < step ? ' is-done' : ''}`}
                disabled={!reachable && index !== step}
                onClick={() => { if (reachable) { setError(''); setStep(index) } }}
              >
                {/* La palomita sustituye al icono en los ya hechos, y sólo al
                    crear: ahí sí hay un avance que contar. Editando los siete
                    pasos existen desde el principio, así que ninguno está «hecho»
                    y todos enseñan lo que son. */}
                <span>{(!editing || completing) && index < step ? <IconCheck size={13} /> : <Icon size={14} />}</span>
                {/* En <em> para poder esconderla en estrecho sin tocar el icono:
                    siete etiquetas no caben, y con la pastilla y el «Paso 3 de 7»
                    de la barra de arriba los iconos bastan. */}
                <em>{t(key)}</em>
              </button>
            )
          })}
          </div>
        </nav>
        )}
        {...(editing && dirty ? {
          closeHidden: true,
          actions: (
            <PendingChangesBar
              saving={saving}
              discardLabel={t('common.cancel')}
              saveLabel={t('svcw.saveChanges')}
              savingLabel={t('svcw.saving')}
              onDiscard={onClose}
              onSave={() => { void create() }}
            />
          ),
        } : {})}
        onClose={onClose}
        busy={saving}
      />


      <div className="dash-svc-wizard-body">
        <form id="svc-form" className="dash-svc-wizard-form" onSubmit={(event) => void submit(event)}>
          <div className="dash-svc-wizard-form-inner">
            {/* Sólo en el primer paso: dicho una vez basta. */}
            {completing && step === 0 ? (
              <p className="dash-ai-pending-note">
                <VelticaMascot outline size={17} />
                <span>{t('aiPending.bookingNote')}</span>
              </p>
            ) : null}
            {step === 0 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('svcw.info.kicker')}</span><h2>{t('svcw.info.title')}</h2><p>{t('svcw.info.lead')}</p>
              {/* Nombre y descripción comparten caja: son el mismo dato
                  —qué es esto— escrito en corto y en largo. Las imágenes van
                  aparte porque se eligen en otro sitio y en otro momento. */}
              <div className="dash-svc-group">
              <label className={`dash-svc-field${invalid?.field === 'title' ? ' is-invalid' : ''}`}>
                <span>{t('svcw.info.name')} <b>*</b></span>
                <input
                  ref={titleRef}
                  onFocus={() => setFocusZone('title')}
                  onBlur={() => setFocusZone(null)}
                  autoFocus
                  value={title}
                  maxLength={200}
                  placeholder={t('svcw.info.namePlaceholder')}
                  aria-invalid={invalid?.field === 'title' || undefined}
                  aria-describedby={invalid?.field === 'title' ? 'svc-err-title' : undefined}
                  onChange={(event) => setTitle(event.target.value)}
                />
                {invalid?.field === 'title'
                  ? <small className="dash-svc-field-error" id="svc-err-title">{invalid.message}</small>
                  : null}
              </label>
              <OfferDescriptionFields shortDescription={shortDescription} description={description}
                onShortDescriptionChange={setShortDescription} onDescriptionChange={setDescription} onFocus={setFocusZone} />
              </div>

              {/* La portada: la foto de lo que se vende. El logo vivía a su
                  lado hasta que la marca se mudó a «Apariencia», donde se elige
                  una vez para toda la página en vez de servicio a servicio. */}
              <div className="dash-svc-group">
              <div className="dash-svc-image-field">
                <div><strong>{t('svcw.info.image')}</strong><span>{t('svcw.info.optional')}</span></div>
                <OfferGalleryField
                  token={token}
                  projectId={projectId}
                  images={images}
                  onChange={setImages}
                  onFocusMedia={(focused) => setFocusZone(focused ? 'media' : null)}
                />
              </div>
              </div>



            </section> : null}

            {step === 1 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('svcw.set.kicker')}</span><h2>{t('svcw.set.title')}</h2><p>{t('svcw.set.lead')}</p>
              {/* Por horas (lo normal) o por días: día completo, un fin de semana,
                  una renta de varios días. */}
              <div className="dash-svc-setting-block">
                <label id="unit-label">{t('svcw.unit.title')}</label>
                <small className="dash-svc-block-hint">{t(unit === 'day' ? 'svcw.unit.dayHint' : 'svcw.unit.timeHint')}</small>
                <div className={`dash-svc-segmented${unit === 'day' ? ' is-second' : ''}`} role="radiogroup" aria-labelledby="unit-label">
                  <button type="button" role="radio" aria-checked={unit === 'time'} className={unit === 'time' ? 'is-on' : ''} onClick={() => setUnit('time')}><IconClock size={14} /> {t('svcw.unit.time')}</button>
                  <button type="button" role="radio" aria-checked={unit === 'day'} className={unit === 'day' ? 'is-on' : ''} onClick={() => setUnit('day')}><IconCalendarEvent size={14} /> {t('svcw.unit.day')}</button>
                </div>
              </div>
              {unit === 'day' ? (
                <div className="dash-svc-setting-block">
                  <label>{t('svcw.days.title')}</label>
                  <small className="dash-svc-block-hint">{t('svcw.days.hint')}</small>
                  <div className="dash-svc-chips">
                    {[1, 2, 3, 7].map((count) => (
                      <button key={count} type="button" className={daysMin === count && !flexDays && !customDaysOpen ? 'is-on' : ''} onClick={() => { setDaysMin(count); setDaysMax(count); setCustomDaysOpen(false) }}>
                        {count === 1 ? t('svcw.days.full') : t('svcw.days.count', { count })}
                      </button>
                    ))}
                    <button type="button" className={customDaysOpen && !flexDays ? 'is-on' : ''} onClick={() => { setCustomDaysOpen(true); setDaysMax(daysMin) }}>{t('svcw.set.other')}</button>
                    <button type="button" className={flexDays ? 'is-on' : ''} onClick={() => { setCustomDaysOpen(false); setDaysMax(Math.max(daysMin + 1, 7)) }}>{t('svcw.days.flexible')}</button>
                  </div>
                  {customDaysOpen && !flexDays ? (
                    <label className="dash-svc-field dash-svc-field-inline">
                      <span>{t('svcw.days.custom')}</span>
                      <span className="dash-svc-suffix">
                        <input
                          autoFocus
                          type="number"
                          min={1}
                          max={30}
                          value={daysMin}
                          onChange={(event) => { const value = Math.max(1, Math.min(30, Number(event.target.value) || 1)); setDaysMin(value); setDaysMax(value) }}
                        />
                        <em>{t('svcw.days.unit')}</em>
                      </span>
                    </label>
                  ) : null}
                  {flexDays ? (
                    <div className="dash-svc-days-range">
                      <label className="dash-svc-field dash-svc-field-inline">
                        <span>{t('svcw.days.from')}</span>
                        <span className="dash-svc-suffix"><input type="number" min={1} max={29} value={daysMin} onChange={(event) => { const value = Math.max(1, Math.min(29, Number(event.target.value) || 1)); setDaysMin(value); if (daysMax <= value) setDaysMax(value + 1) }} /><em>{t('svcw.days.unit')}</em></span>
                      </label>
                      <label className="dash-svc-field dash-svc-field-inline">
                        <span>{t('svcw.days.to')}</span>
                        <span className="dash-svc-suffix"><input type="number" min={daysMin + 1} max={30} value={daysMax} onChange={(event) => setDaysMax(Math.max(daysMin + 1, Math.min(30, Number(event.target.value) || daysMin + 1)))} /><em>{t('svcw.days.unit')}</em></span>
                      </label>
                      <small className="dash-svc-block-hint">{t('svcw.days.perDay')}</small>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="dash-svc-setting-block"><label>{t('svcw.set.duration')}</label><small className="dash-svc-block-hint">{t('svcw.set.durationHint')}</small><div className="dash-svc-chips">
                {/* También en las pastillas: pulsarlas las enfoca, así que la
                    vista se acerca a la línea de datos justo cuando se cambia el
                    valor que vive ahí. */}
                {PRESETS.map((minutes) => <button key={minutes} type="button" className={duration === minutes && !customDurationOpen ? 'is-on' : ''} onFocus={() => setFocusZone('facts')} onBlur={() => setFocusZone(null)} onClick={() => { setDuration(minutes); setCustomDuration(''); setCustomDurationOpen(false) }}>{durationLabel(minutes, t)}</button>)}
                <button type="button" className={customDurationOpen ? 'is-on' : ''} onClick={() => { setCustomDurationOpen(true); setCustomDuration('') }}>{t('svcw.set.other')}</button>
              </div>{customDurationOpen ? <label className={`dash-svc-field dash-svc-field-inline${invalid?.field === 'duration' ? ' is-invalid' : ''}`}><span>{t('svcw.set.otherDuration')}</span><span className="dash-svc-suffix"><input ref={durationRef} aria-invalid={invalid?.field === 'duration' || undefined} onFocus={() => setFocusZone('facts')} onBlur={() => setFocusZone(null)} autoFocus type="number" min={5} max={1440} step={5} placeholder="—" value={customDuration} onChange={(event) => { setCustomDuration(event.target.value); const parsed = Number(event.target.value); if (parsed >= 5 && parsed <= 1440) setDuration(parsed) }} /><em>{t('svcw.set.minutes')}</em></span>{invalid?.field === 'duration' ? <small className="dash-svc-field-error">{invalid.message}</small> : null}</label> : null}</div>
              )}
              {/* Individual o grupal, no «un número de cupo». Nadie piensa
                  «pongo 1» para dar una consulta: piensa que es de uno en uno.
                  El número sólo aparece cuando de verdad hay que decidirlo. */}
              <div className="dash-svc-setting-block">
                <label id="cupo-label">{t('svcw.set.capacity')}</label>
                <small className="dash-svc-block-hint">{t('svcw.set.capacityHint')}</small>
                <div className={`dash-svc-segmented${isGroup ? ' is-second' : ''}`} role="radiogroup" aria-labelledby="cupo-label">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isGroup}
                    className={!isGroup ? 'is-on' : ''}
                    onClick={() => setCapacity(1)}
                  ><IconUser size={14} /> {t('svcw.set.single')}</button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isGroup}
                    className={isGroup ? 'is-on' : ''}
                    onClick={() => setCapacity(Math.max(2, lastGroupSize.current))}
                  ><IconUsers size={14} /> {t('svcw.set.group')}</button>
                </div>
                {isGroup ? (
                  <div className="dash-svc-capacity-grid">
                    <div className="dash-svc-capacity-card">
                      <label className="dash-svc-field dash-svc-field-inline dash-svc-capacity-field">
                        <span>{t('svcw.set.perSlot')}</span>
                        <span className="dash-svc-suffix">
                          <input
                            autoFocus
                            type="number"
                            min={2}
                            max={1000}
                            value={capacityDraft ?? capacity}
                            onFocus={() => setFocusZone('facts')}
                            onBlur={() => {
                              if (capacityDraft !== null && capacityDraft.trim() !== '') {
                                const parsed = Number(capacityDraft)
                                if (Number.isFinite(parsed)) setCapacity(Math.max(2, Math.min(1000, Math.trunc(parsed))))
                              }
                              setCapacityDraft(null)
                              setFocusZone(null)
                            }}
                            onChange={(event) => {
                              const raw = event.target.value
                              setCapacityDraft(raw)
                              const parsed = Number(raw)
                              if (raw !== '' && Number.isInteger(parsed) && parsed >= 2 && parsed <= 1000) setCapacity(parsed)
                            }}
                          />
                          <em>{t('svcw.set.people')}</em>
                        </span>
                        <small>{t('svcw.set.seatsHint')}</small>
                      </label>
                    </div>
                    <div className="dash-svc-capacity-card">
                      <label className="dash-svc-field dash-svc-field-inline dash-svc-capacity-field">
                        <span>{t('svcw.set.maxParty')}</span>
                        <span className="dash-svc-suffix">
                          <input
                            type="number"
                            min={1}
                            max={capacity}
                            value={Math.min(maxParty, capacity)}
                            onChange={(event) => setMaxParty(Math.max(1, Math.min(capacity, Number(event.target.value) || 1)))}
                          />
                          <em>{t('svcw.set.people')}</em>
                        </span>
                        <small>{t('svcw.set.maxPartyHint')}</small>
                      </label>
                    </div>
                  </div>
                ) : null}
                {isGroup ? (
                  <div className="dash-svc-spots">
                    <span className="dash-svc-pricing-label" id="spots-label">{t('svcw.spots.title')}</span>
                    <div className="dash-svc-spots-options" role="radiogroup" aria-labelledby="spots-label">
                      {(['low', 'all', 'none'] as const).map((mode) => (
                        <button key={mode} type="button" role="radio" aria-checked={spotsDisplay === mode} className={spotsDisplay === mode ? 'is-on' : ''} onClick={() => setSpotsDisplay(mode)}>
                          <strong>{t(`svcw.spots.${mode}`)}</strong>
                          <small>{t(`svcw.spots.${mode}Hint`)}</small>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="dash-svc-schedule-note"><IconUser size={14} /> {t('svcw.set.onePerSlot')}</p>
                )}
              </div>

              {/* El recordatorio. Aquí y no en «Cancelación»: manda también en
                  los servicios que no cobran ni tienen plazo, y es un ajuste de
                  la cita, como su duración. */}
              <div className="dash-svc-setting-block">
                <label>{t('svcw.set.reminder')}</label>
                <small className="dash-svc-block-hint">{t('svcw.set.reminderHint')}</small>
                {/* Dos filas sencillas: la casilla y, si está puesta, las horas. */}
                <div className="dash-svc-reminder-row">
                  <label className="dash-svc-reminder-check">
                    <input type="checkbox" checked={reminderEnabled} onChange={(event) => setReminderEnabled(event.target.checked)} />
                    <span><strong>{t('svcw.set.reminderOn')}</strong><small>{t('svcw.set.reminderOnHint')}</small></span>
                  </label>
                </div>
                {reminderEnabled ? (
                  <div className="dash-svc-reminder-row">
                    <label className="dash-svc-reminder-hours">
                      <span>{t('svcw.set.reminderSend')}</span>
                      <input
                        type="number" min={1} max={168} step={1}
                        value={reminderHours}
                        onChange={(event) => setReminderHours(event.target.value)}
                        onBlur={() => setReminderHours(clampField(reminderHours || '24', { min: 1, max: 168 }))}
                      />
                      <em>{t('svcw.cancel.hoursBefore')}</em>
                    </label>
                    <small>{t('svcw.set.reminderSkip')}</small>
                  </div>
                ) : null}
              </div>

            </section> : null}

            {step === 3 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('svcw.form.kicker')}</span>
              <h2>{t('svcw.form.title')}</h2>
              <p>{t('svcw.form.lead')}</p>

              <div className="dash-svc-fixed-fields">
                <span className="dash-svc-fixed-legend">{t('svcw.form.included')}</span>
                <div className="dash-svc-fixed-grid">
                  <span><IconCheck size={13} /> {t('svcw.form.name')}</span>
                  <span><IconCheck size={13} /> {t('svcw.form.email')}</span>
                  <span><IconCheck size={13} /> {t('svcw.form.phone')}</span>
                </div>
                <small>{t('svcw.form.noSetup')}</small>
              </div>

              <BookingFormFieldsEditor
                plain
                fields={formFields}
                saving={false}
                onChange={setFormFields}
                onIncomplete={setPreguntasAMedias}
                revealMissing={señalarPreguntas}
              />

            </section> : null}

            {step === 4 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('svcw.sch.kicker')}</span><h2>{t('svcw.sch.title')}</h2>
              {completing ? <p className="dash-ai-pending-hint">{t('aiPending.scheduleHint')}</p> : null}
              {/*
                * La zona horaria se dice SÓLO cuando no es la tuya.
                *
                * Antes salía siempre, y en crudo: «Las horas se muestran en
                * America/Los_Angeles». A quien está en esa zona no le aportaba
                * nada —son sus horas— y a quien no, le decía el problema con el
                * nombre de una base de datos y sin decirle qué hacer.
                *
                * Y el problema es real, no cosmético: si el sitio guarda su
                * horario en otra zona, el 9:00 que se escribe aquí no es el 9:00
                * que verá quien reserve. Por eso cuando difiere se avisa con las
                * dos zonas y el camino para cambiarlo, y cuando coincide
                * desaparece.
                */}
              {scheduleZone && READER_ZONE && scheduleZone !== READER_ZONE ? (
                <p className="dash-svc-zone-warn">
                  <IconAlertTriangle size={15} aria-hidden="true" />
                  {/* Partido porque las dos zonas van en negrita en medio de la
                      frase: una clave con marcado dentro no la traduce nadie sin
                      escribir HTML. */}
                  <span>
                    {t('svcw.sch.zoneNote1')} <strong>{zoneLabel(scheduleZone)}</strong>{' '}
                    {t('svcw.sch.zoneNote2')} <strong>{zoneLabel(READER_ZONE)}</strong>
                    {t('svcw.sch.zoneNote3', { city: zoneLabel(scheduleZone).split(' (')[0] ?? '' })}
                    {' '}{t('svcw.sch.settingsPath')}
                  </span>
                </p>
              ) : (
                <p>{t('svcw.sch.localNote')}</p>
              )}
              {/* Siempre a la vista, también con una sola persona: escondido,
                  nadie sabía que un servicio puede darlo más de uno. */}
              {activeTeam.length ? (
                <div className="dash-svc-setting-block dash-svc-team">
                  <label id="team-label">{t('svcw.team.who')}</label>
                  <small className="dash-svc-block-hint">{t(activeTeam.length > 1 ? 'svcw.team.whoHint' : 'svcw.team.onlyOne')}</small>
                  {/* Una fila por persona, como en Equipo: su cara, su nombre y su
                      puesto, y un interruptor que dice que se puede quitar. */}
                  <ul className="dash-svc-team-members" aria-labelledby="team-label">
                    {activeTeam.map((member) => {
                      const on = teamIds.includes(member.id)
                      return (
                        <li key={member.id} className={on ? 'is-on' : ''}>
                          <TeamPhotoButton
                            projectId={projectId}
                            token={token}
                            member={member}
                            className="dash-svc-team-photo"
                            onChanged={(photoUrl) => onResourcesChange?.(resources.map((row) => (row.id === member.id ? { ...row, photoUrl } : row)))}
                          />
                          <span className="dash-svc-team-copy">
                            <strong>{member.name}</strong>
                            {member.role ? <small>{member.role}</small> : null}
                          </span>
                          <button
                            type="button"
                            className={`dash-general-toggle${on ? ' is-on' : ''}`}
                            role="switch"
                            aria-checked={on}
                            aria-label={member.name}
                            /* Siempre alguien: un servicio sin nadie no se puede reservar. */
                            disabled={on && teamIds.length <= 1}
                            onClick={() => toggleTeamMember(member.id)}
                          >
                            <span />
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                  {addingMember ? (
                    <div className="dash-svc-team-add">
                      <input
                        autoFocus
                        value={memberName}
                        maxLength={80}
                        placeholder={t('svcw.team.addName')}
                        aria-label={t('svcw.team.addName')}
                        onChange={(event) => setMemberName(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addMember() } }}
                        disabled={memberBusy}
                      />
                      <input
                        value={memberRole}
                        maxLength={80}
                        placeholder={t('svcw.team.addRole')}
                        aria-label={t('svcw.team.addRole')}
                        onChange={(event) => setMemberRole(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addMember() } }}
                        disabled={memberBusy}
                      />
                      <div className="dash-svc-team-add-actions">
                        <button type="button" className="dash-ghost-btn" onClick={() => { setAddingMember(false); setMemberError('') }} disabled={memberBusy}>
                          {t('svcw.team.addCancel')}
                        </button>
                        <button type="button" className="dash-primary-btn" onClick={() => void addMember()} disabled={memberBusy || !memberName.trim()}>
                          {memberBusy ? <IconLoader2 className="dash-spin" size={14} aria-hidden="true" /> : <IconPlus size={14} aria-hidden="true" />}
                          {t('svcw.team.addSave')}
                        </button>
                      </div>
                      {memberError ? <p className="dash-svc-team-add-error" role="alert">{memberError}</p> : null}
                    </div>
                  ) : (
                    <button type="button" className="dash-svc-team-add-open" onClick={() => setAddingMember(true)}>
                      <IconPlus size={14} aria-hidden="true" /> {t('svcw.team.add')}
                    </button>
                  )}
                  {/* Con más de una persona: a quién va la cita cuando se elige
                      «Cualquiera disponible». Un selector: es un ajuste de una
                      vez, y debajo se explica la opción elegida. */}
                  {teamIds.length > 1 ? (
                    <div className="dash-svc-assignment">
                      <div className="dash-svc-assignment-copy">
                        <span className="dash-svc-assignment-label" id="assignment-label">
                          {t('bookings.team.assignmentTitle')}
                          <InfoHint text={t('bookings.team.assignmentHint')} />
                        </span>
                        <small>{t(`bookings.team.rule.${assignment}.hint`)}</small>
                      </div>
                      <span className="dash-svc-assignment-select">
                        <select aria-labelledby="assignment-label" value={assignment} onChange={(event) => setAssignment(event.target.value as BookingAssignment)}>
                          {ASSIGNMENTS.map((rule) => (
                            <option key={rule} value={rule}>{t(`bookings.team.rule.${rule}`)}</option>
                          ))}
                        </select>
                        <IconChevronDown size={14} aria-hidden="true" />
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {/* De quién es el horario: pestañas de carpeta pegadas al editor.
                  Con pastillas sueltas en otra tarjeta no se entendía que el
                  horario de abajo cambiaba con ellas; como carpeta, la pestaña
                  abierta y su contenido son una sola pieza. */}
              <div className={teamIds.length > 1 ? 'dash-svc-folder' : undefined}>
              {teamIds.length > 1 ? (
                <div className="dash-svc-folder-bar">
                  <div className="dash-svc-folder-head">
                    <strong id="schedule-of-label">{t('svcw.team.scheduleOf')}</strong>
                    {resource ? <small>{t('svcw.team.editingHint', { name: resource.name })}</small> : null}
                  </div>
                  <div className="dash-svc-folder-tabs" role="tablist" aria-labelledby="schedule-of-label">
                    {teamIds.map((id) => {
                      const member = resources.find((row) => row.id === id)
                      if (!member) return null
                      const on = scheduleOwnerId === id
                      return (
                        <button key={id} type="button" role="tab" aria-selected={on} className={on ? 'is-on' : ''} onClick={() => editScheduleOf(id)}>
                          <span className="dash-svc-team-photo" aria-hidden="true">{(member.photoUrl ? <img src={member.photoUrl} alt="" /> : member.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase())}</span>
                          <span>{member.name}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}
              <div className={teamIds.length > 1 ? 'dash-svc-folder-body' : undefined} role={teamIds.length > 1 ? 'tabpanel' : undefined}>
              <div className="dash-svc-schedule-editor">
                <div className="dash-svc-schedule-mode">
                  <div className="dash-svc-schedule-mode-head">
                    <strong id="schedule-editor-label">{t('svcw.sch.howTitle')}</strong>
                    <small>{t(scheduleEditorMode === 'simple' ? 'svcw.sch.howSimple' : 'svcw.sch.howAdvanced')}</small>
                  </div>
                  <div className={`dash-svc-segmented dash-svc-schedule-tabs${scheduleEditorMode === 'advanced' ? ' is-second' : ''}`} role="radiogroup" aria-labelledby="schedule-editor-label">
                    <button type="button" role="radio" aria-checked={scheduleEditorMode === 'simple'} className={scheduleEditorMode === 'simple' ? 'is-on' : ''} onClick={() => chooseScheduleEditor('simple')}>
                      {t('svcw.sch.simple')}
                    </button>
                    <button type="button" role="radio" aria-checked={scheduleEditorMode === 'advanced'} className={scheduleEditorMode === 'advanced' ? 'is-on' : ''} onClick={() => chooseScheduleEditor('advanced')}>
                      {t('svcw.sch.more')}
                    </button>
                  </div>
                </div>

                {scheduleEditorMode === 'simple' ? (
                  compactSchedule ? (
                    <div className="dash-svc-simple-schedule">
                      <div className="dash-svc-simple-days" role="group" aria-label={t('svcw.sch.daysAria')}>
                        {WEEKDAYS.map((label, weekday) => {
                          const open = compactSchedule.weekdays.includes(weekday)
                          return (
                            <button key={weekday} type="button" aria-pressed={open} className={open ? 'is-on' : ''} onClick={() => toggleSimpleDay(weekday)}>
                              {t(label)}
                            </button>
                          )
                        })}
                      </div>
                      <div className="dash-svc-simple-range">
                        <label>
                          <span>{t('svcw.sch.from')}</span>
                          <TimeControl
                            label={t('svcw.sch.from')}
                            value={minutesToTime(compactRange.startMinute)}
                            onChange={(value) => patchSimpleRange('startMinute', value)}
                          />
                        </label>
                        <span aria-hidden="true">{t('svcw.sch.rangeSep')}</span>
                        <label>
                          <span>{t('svcw.sch.to')}</span>
                          <TimeControl
                            label={t('svcw.sch.to')}
                            value={minutesToTime(compactRange.endMinute)}
                            onChange={(value) => patchSimpleRange('endMinute', value)}
                          />
                        </label>
                      </div>
                      {!compactSchedule.weekdays.length ? <p>{t('svcw.sch.pickDay')}</p> : null}
                    </div>
                  ) : (
                    <div className="dash-svc-simple-unavailable" role="status">
                      <IconClock size={18} aria-hidden="true" />
                      <span><strong>{t('svcw.sch.multiRange')}</strong> {t('svcw.sch.multiRangeBody')}</span>
                      <button type="button" onClick={() => chooseScheduleEditor('advanced')}>{t('svcw.sch.openAdvanced')}</button>
                    </div>
                  )
                ) : (
                  <div className="dash-svc-week-editor">{WEEKDAYS.map((label, weekday) => {
                    const dayRules = rules.map((rule, index) => ({ rule, index })).filter(({ rule }) => rule.weekday === weekday)
                    return <div className={`dash-svc-week-row${dayRules.length ? ' is-open' : ''}`} key={weekday}>
                      <label className="dash-svc-day-toggle"><input type="checkbox" checked={dayRules.length > 0} onChange={(event) => toggleDay(weekday, event.target.checked)} /><span /><strong>{t(label)}</strong></label>
                      {dayRules.length ? <div className="dash-svc-day-ranges">{dayRules.map(({ rule, index }) => <div className="dash-svc-time-range" key={index}><TimeControl label={t('svcw.sch.dayFrom', { day: t(label) })} value={minutesToTime(rule.startMinute)} onChange={(value) => patchRule(index, 'startMinute', value)} /><span>{t('svcw.sch.rangeSep')}</span><TimeControl label={t('svcw.sch.dayTo', { day: t(label) })} value={minutesToTime(rule.endMinute)} onChange={(value) => patchRule(index, 'endMinute', value)} />{dayRules.length > 1 ? <button type="button" className="dash-svc-remove-range" aria-label={t('svcw.sch.removeRange', { day: t(label) })} onClick={() => setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))}><IconX size={13} /></button> : null}</div>)}<button type="button" className="dash-svc-add-range" onClick={() => setRules((current) => [...current, { weekday, startMinute: 540, endMinute: 1020 }])}><IconPlus size={12} /> {t('svcw.sch.anotherRange')}</button></div> : <span className="dash-svc-closed">{t('svcw.sch.closed')}</span>}
                    </div>
                  })}</div>
                )}
              </div>

              {/*
                Lo fino, aquí y plegado.

                Estaba en «Ajustes», junto a la duración y el cupo, y ahí no
                pintaba nada: márgenes, antelación y horizonte no describen el
                servicio —describen CUÁNDO se puede reservar—, que es de lo que
                trata este paso. Y plegado porque sus valores por defecto sirven
                para casi cualquier negocio: a la vista convertían el horario en
                un formulario de siete campos.
              */}
              {/*
                Las excepciones van ANTES que los ajustes finos y después de la
                semana, que es el orden en que se piensa: ésta es mi semana, estos
                días no, y ya al final los detalles. Sin plegar, porque una
                excepción olvidada vende una cita el día que estás de vacaciones.
              */}
              <details className="dash-svc-exceptions dash-svc-fold">
                <summary className="dash-svc-fold-head">
                  <span className="dash-svc-fold-text">
                    <strong>{t('svcw.sch.exceptions')}</strong>
                    <em>{t('svcw.sch.exceptionsHint')}</em>
                    <span className="dash-svc-fold-value">{exceptionsSummary}</span>
                  </span>
                  <span className="dash-svc-fold-toggle" aria-hidden="true"><IconChevronDown size={15} /></span>
                </summary>
                <div className="dash-svc-exceptions-body">
                  {exceptions.length ? (
                    <ul className="dash-svc-exceptions-list">
                      {exceptions
                        .map((exception, index) => ({ exception, index }))
                        .sort((a, b) => a.exception.day.localeCompare(b.exception.day))
                        .map(({ exception, index }) => (
                          <li key={exception.id ?? `${exception.day}-${index}`}>
                            <input
                              type="date"
                              aria-label={t('svcw.sch.day')}
                              value={exception.day}
                              onChange={(event) => patchException(index, { day: event.target.value })}
                            />
                            <select
                              aria-label={t('svcw.sch.whatHappens')}
                              value={exceptionMode(exception)}
                              onChange={(event) => patchException(index, EXCEPTION_MODES[event.target.value as ExceptionMode])}
                            >
                              {/* Tres, no dos, y dicen lo que hacen de verdad.
                                  «Abierto sólo de» prometía una restricción que el
                                  motor no hace: las horas sueltas se SUMAN a las de
                                  la semana, así que en un día que ya abría no
                                  cambiaba nada y parecía que el ajuste no servía.
                                  Restringir es cerrar un rato, y para eso faltaba
                                  la opción de en medio. */}
                              <option value="closed-day">{t('svcw.sch.closedDay')}</option>
                              <option value="closed-range">{t('svcw.sch.closedRange')}</option>
                              <option value="open">{t('svcw.sch.openRange')}</option>
                            </select>
                            {exception.startMinute !== null ? (
                              <span className="dash-svc-time-range">
                                <TimeControl label={t('svcw.sch.from')} value={minutesToTime(exception.startMinute ?? 540)}
                                  onChange={(value) => patchException(index, { startMinute: timeToMinutes(value) })} />
                                <span>{t('svcw.sch.at')}</span>
                                <TimeControl label={t('svcw.sch.to')} value={minutesToTime(exception.endMinute ?? 1020)}
                                  onChange={(value) => patchException(index, { endMinute: timeToMinutes(value) })} />
                              </span>
                            ) : null}
                            <input
                              type="text"
                              className="dash-svc-exceptions-note"
                              aria-label={t('svcw.sch.note')}
                              placeholder={t('svcw.sch.notePlaceholder')}
                              value={exception.note}
                              onChange={(event) => patchException(index, { note: event.target.value })}
                            />
                            <button type="button" className="dash-svc-remove-range" aria-label={t('svcw.sch.removeException')}
                              onClick={() => setExceptions((current) => current.filter((_, position) => position !== index))}>
                              <IconX size={13} />
                            </button>
                            <small className="dash-svc-exceptions-hint">{exceptionHint(exception)}</small>
                          </li>
                        ))}
                    </ul>
                  ) : null}
                  {/* Sin ninguna no se anuncia el vacío: «Ninguna.» ocupaba un
                      renglón para decir lo que ya se ve, y encima empujaba hacia
                      abajo lo único que hay que pulsar. */}
                  <button type="button" className="dash-secondary-btn dash-svc-exceptions-add" onClick={addException}>
                    <IconPlus size={15} /> {t('svcw.sch.addException')}
                  </button>
                </div>
              </details>

              <details className="dash-svc-exceptions dash-svc-fold">
                <summary className="dash-svc-fold-head">
                  <span className="dash-svc-fold-text">
                    <strong>{t('svcw.sch.advanced')}</strong>
                    <em>{t('svcw.sch.advancedHint')}</em>
                    <span className="dash-svc-fold-value">{advancedSummary}</span>
                  </span>
                  <span className="dash-svc-fold-toggle" aria-hidden="true"><IconChevronDown size={15} /></span>
                </summary>
                <div className="dash-svc-advanced-body">
                {/*
                  Cada ayuda traduce el VALOR PUESTO, no un ejemplo inventado.
                  «Con 120, lo más pronto es dentro de dos horas» sólo sirve si
                  tienes 120; con cualquier otro número hay que hacer la cuenta de
                  cabeza. Ahora la hace la ayuda: escribes 90 y lee «hora y media
                  antes».
                */}
                {ADVANCED.map((field) => {
                  const value = field.get()
                  return (
                    <div className="dash-svc-advanced-item" key={field.label}>
                    <label className="dash-svc-field dash-svc-field-inline">
                      <span>{field.label}</span>
                      <span className="dash-svc-suffix">
                        <input
                          type="number"
                          min={field.min}
                          max={field.max}
                          step={field.step}
                          {...(field.placeholder ? { placeholder: field.placeholder } : {})}
                          value={value}
                          onChange={(event) => field.set(event.target.value)}
                          /* El tope se aplica al salir, no al teclear: recortando
                             en cada tecla, escribir «120» en un campo que llega a
                             90 se convertía en 9 al pulsar el 1 y luego en 90 —el
                             número saltaba bajo el dedo. */
                          onBlur={() => field.set(clampField(value, field))}
                        />
                        <em>{field.unit}</em>
                      </span>
                      <small>{field.hint(value)}</small>
                    </label>
                    </div>
                  )
                })}
                </div>
              </details>
              {/* Los ajustes finos también dentro del contenedor: el paso entero
                  se lee como una sola pieza, aunque éstos sean del servicio. */}
              </div>
              </div>
            </section> : null}

            {step === 5 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('svcw.cancel.kicker')}</span>
              <h2>{t('svcw.cancel.title')}</h2>
              <p>{t('svcw.cancel.lead')}</p>

              {/*
                Su propio paso, y no un apartado del horario.
                Compartiendo pantalla con las franjas de la semana quedaba
                debajo de siete días desplegados: nadie bajaba hasta ahí, y la
                decisión más delicada del servicio —qué haces cuando alguien te
                falla— se tomaba por omisión. Y no va con el precio porque manda
                también cuando la cita es gratis.
              */}
              {/*
                Plegadas, y sólo la primera abierta.
                Son tres decisiones distintas —cuándo, cuánto y qué le ofreces—
                con su explicación cada una, y desplegadas de golpe suman una
                pantalla y media que nadie lee entera. Abiertas por turnos se
                deciden de una en una, que es como se piensan.
              */}
              <details className="dash-svc-exceptions dash-svc-fold">
                <summary className="dash-svc-fold-head">
                  <span className="dash-svc-fold-icon" aria-hidden="true"><IconClock size={15} /></span>
                  <span className="dash-svc-fold-text">
                    <strong>{t('svcw.cancel.windows')}</strong>
                    <em>{t('svcw.cancel.windowsHint')}</em>
                    {/* Lo que hay puesto, bajo su propia explicación: leído en esa
                        columna se entiende como el valor de LO QUE ACABA de
                        describirse. Suelto a la derecha había que emparejarlo con el
                        título de al lado para saber de qué hablaba. */}
                    <span className="dash-svc-fold-value">{whenSummary}</span>
                  </span>
                  <span className="dash-svc-fold-toggle" aria-hidden="true"><IconChevronDown size={15} /></span>
                </summary>
                <div className="dash-svc-exceptions-body dash-svc-windows">
                  <label className="dash-svc-field dash-svc-field-inline">
                    <span>{t('svcw.cancel.cancelUntil')}</span>
                    <span className="dash-svc-suffix">
                      <input
                        type="number" min={0} max={720} step={1}
                        value={cancelWindow}
                        onChange={(event) => setCancelWindow(event.target.value)}
                        onBlur={() => setCancelWindow(clampField(cancelWindow || '0', { min: 0, max: 720 }))}
                      />
                      <em>{t('svcw.cancel.hoursBefore')}</em>
                    </span>
                    <small>{windowHint(cancelWindow, 'cancel')}</small>
                  </label>

                  <label className="dash-svc-field dash-svc-field-inline">
                    <span>{t('svcw.cancel.moveUntil')}</span>
                    <span className="dash-svc-suffix">
                      <input
                        type="number" min={0} max={720} step={1}
                        placeholder={cancelWindow || '0'}
                        value={moveWindow}
                        onChange={(event) => setMoveWindow(event.target.value)}
                        onBlur={() => setMoveWindow(moveWindow.trim() ? clampField(moveWindow, { min: 0, max: 720 }) : '')}
                      />
                      <em>{t('svcw.cancel.hoursBefore')}</em>
                    </span>
                    <small>
                      {moveWindow.trim()
                        ? windowHint(moveWindow, 'move')
                        : t('svcw.cancel.moveEmpty')}
                    </small>
                  </label>

                  {/*
                    Y qué pasa cuando el plazo ya se pasó.
                    Obligar a asistir no produce asistencia, produce ausencias sin
                    avisar: el mismo hueco perdido, sin margen para revenderlo y
                    con una mala reseña de regalo. Por eso el defecto es que te
                    escriban, que deja la decisión en una persona con criterio.
                  */}
                </div>
              </details>

              <details className="dash-svc-exceptions dash-svc-fold">
                <summary className="dash-svc-fold-head">
                  <span className="dash-svc-fold-icon" aria-hidden="true"><IconCoin size={15} /></span>
                  <span className="dash-svc-fold-text">
                    <strong>{t('svcw.cancel.refund')}</strong>
                    <em>{t('svcw.cancel.refundHint')}</em>
                    {/* Lo que hay puesto, bajo su propia explicación: leído en esa
                        columna se entiende como el valor de LO QUE ACABA de
                        describirse. Suelto a la derecha había que emparejarlo con el
                        título de al lado para saber de qué hablaba. */}
                    <span className="dash-svc-fold-value">{refundSummary}</span>
                  </span>
                  <span className="dash-svc-fold-toggle" aria-hidden="true"><IconChevronDown size={15} /></span>
                </summary>
                <div className="dash-svc-exceptions-body">
                  {/*
                    El dinero, en dos preguntas y no en una matriz de tramos.
                    «El 100 hasta 72 h, el 50 hasta 24, el 0 después» es más fino
                    y es justo lo que hace que nadie toque la pantalla y se quede
                    con lo que venga puesto.
                  */}
                  <div className="dash-svc-late">
                    {paymentMode === 'onsite' || included ? (
                      <p className="dash-svc-late-note">{t(included ? 'svcw.cancel.membershipNote' : 'svcw.cancel.onsiteNote')}</p>
                    ) : (
                      <div className="dash-svc-refunds">
                        {([
                          ['svcw.cancel.inTime', refundPercent, setRefundPercent, refundHint],
                          ['svcw.cancel.outOfTime', refundLate, setRefundLate, refundHint],
                        ] as const).map(([label, value, set, hint]) => (
                          <label className="dash-svc-field dash-svc-field-inline" key={label}>
                            <span>{t(label)}</span>
                            <span className="dash-svc-suffix">
                              {/* Un número y no tres opciones: el 70 y el 25 son
                                  políticas que existen, y con una lista cerrada
                                  había que elegir la de al lado. */}
                              <input
                                type="number" min={0} max={100} step={5}
                                value={value}
                                onChange={(event) => set(Number(event.target.value))}
                                onBlur={(event) => set(Math.min(100, Math.max(0, Math.round(Number(event.target.value)) || 0)))}
                              />
                              <em>%</em>
                            </span>
                            <small>{hint(value)}</small>
                          </label>
                        ))}

                        <div className="dash-svc-refund-who">
                          <span className="dash-svc-pricing-label">{t('svcw.cancel.refundWho')}</span>
                          <div className="dash-svc-late-choices">
                            {([
                              [false, 'svcw.cancel.refundManual', 'svcw.cancel.refundManualHint'],
                              [true, 'svcw.cancel.refundAuto', 'svcw.cancel.refundAutoHint'],
                            ] as const).map(([value, label, hint]) => (
                              <label key={String(value)} className={refundAuto === value ? 'is-on' : ''}>
                                <input type="radio" name="refund-who" checked={refundAuto === value} onChange={() => setRefundAuto(value)} />
                                <span><strong>{t(label)}</strong><small>{t(hint)}</small></span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <p className="dash-svc-late-note">{t('svcw.cancel.noShow')}</p>
                      </div>
                    )}
                  </div>

                </div>
              </details>

              <details className="dash-svc-exceptions dash-svc-fold">
                <summary className="dash-svc-fold-head">
                  <span className="dash-svc-fold-icon" aria-hidden="true"><IconLifebuoy size={15} /></span>
                  <span className="dash-svc-fold-text">
                    <strong>{t('svcw.cancel.late')}</strong>
                    <em>{t('svcw.cancel.lateHint')}</em>
                    {/* Lo que hay puesto, bajo su propia explicación: leído en esa
                        columna se entiende como el valor de LO QUE ACABA de
                        describirse. Suelto a la derecha había que emparejarlo con el
                        título de al lado para saber de qué hablaba. */}
                    <span className="dash-svc-fold-value">{lateSummary}</span>
                  </span>
                  <span className="dash-svc-fold-toggle" aria-hidden="true"><IconChevronDown size={15} /></span>
                </summary>
                <div className="dash-svc-exceptions-body">
                  <div className="dash-svc-late">
                    <div className="dash-svc-late-choices">
                      {([
                        ['contact', 'svcw.cancel.lateContact', 'svcw.cancel.lateContactHint'],
                        ['move', 'svcw.cancel.lateMove', 'svcw.cancel.lateMoveHint'],
                        ['none', 'svcw.cancel.lateNone', 'svcw.cancel.lateNoneHint'],
                      ] as const).map(([value, label, hint]) => (
                        <label key={value} className={latePolicy === value ? 'is-on' : ''}>
                          <input type="radio" name="late-policy" checked={latePolicy === value} onChange={() => setLatePolicy(value)} />
                          <span><strong>{t(label)}</strong><small>{t(hint)}</small></span>
                        </label>
                      ))}
                    </div>

                    <div className="dash-svc-late-switches">
                      <label>
                        <input type="checkbox" checked={refillRefund} onChange={(event) => setRefillRefund(event.target.checked)} />
                        <span><strong>{t('svcw.cancel.refill')}</strong><small>{t('svcw.cancel.refillHint2')}</small></span>
                      </label>
                      <label>
                        <input type="checkbox" checked={lateCredit} onChange={(event) => setLateCredit(event.target.checked)} />
                        <span><strong>{t('svcw.cancel.credit')}</strong><small>{t('svcw.cancel.creditHint')}</small></span>
                      </label>
                      <label>
                        <input type="checkbox" checked={forgiveFirst} onChange={(event) => setForgiveFirst(event.target.checked)} />
                        <span><strong>{t('svcw.cancel.forgive')}</strong><small>{t('svcw.cancel.forgiveHint2')}</small></span>
                      </label>
                    </div>

                    {/* Honesto sobre qué hace hoy cada cosa: el plazo ya manda de
                        verdad; estos tres todavía sólo se anuncian. */}
                    <p className="dash-svc-late-note">{t('svcw.cancel.footNote')}</p>
                  </div>
                </div>
              </details>

              {/*
                A quién escribe el cliente.

                Aquí y no en «Información» porque es aquí donde hace falta:
                «escríbenos» es una de las tres salidas que se le ofrecen a quien
                avisa tarde, y sin un correo esa salida no lleva a ninguna parte.
                Vacío hereda el correo de avisos del proyecto, así que no es un
                campo obligatorio — es uno para cuando ESTE servicio se atiende
                desde otro sitio.
              */}
              <details className="dash-svc-exceptions dash-svc-fold">
                <summary className="dash-svc-fold-head">
                  <span className="dash-svc-fold-icon" aria-hidden="true"><IconAt size={15} /></span>
                  <span className="dash-svc-fold-text">
                    <strong>{t('svcw.cancel.contact')}</strong>
                    <em>{t('svcw.cancel.contactHint')}</em>
                    <span className="dash-svc-fold-value">{contactEmail.trim() || t('svcw.cancel.contactDefault')}</span>
                  </span>
                  <span className="dash-svc-fold-toggle" aria-hidden="true"><IconChevronDown size={15} /></span>
                </summary>
                <div className="dash-svc-fold-body">
                  <label className={`dash-svc-field${invalid?.field === 'contactEmail' ? ' is-invalid' : ''}`}>
                    <span>{t('bookings.svc.detail.email')}</span>
                    <input
                      type="email"
                      inputMode="email"
                      placeholder={t('bookings.svc.detail.emailPlaceholder')}
                      value={contactEmail}
                      aria-invalid={invalid?.field === 'contactEmail' || undefined}
                      onChange={(event) => setContactEmail(event.target.value)}
                    />
                    {invalid?.field === 'contactEmail'
                      ? <small className="dash-svc-field-error">{invalid.message}</small>
                      : <small>{t('bookings.svc.detail.emailHelp')}</small>}
                  </label>
                  <label className="dash-svc-field">
                    <span>{t('bookings.svc.detail.phone')}</span>
                    <input
                      type="tel"
                      inputMode="tel"
                      placeholder={t('bookings.svc.detail.phonePlaceholder')}
                      value={contactPhone}
                      onChange={(event) => setContactPhone(event.target.value)}
                    />
                    <small>{t('bookings.svc.detail.phoneHelp')}</small>
                  </label>
                </div>
              </details>

            </section> : null}

            {step === 2 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('svcw.price.kicker')}</span><h2>{t('svcw.price.title')}</h2><p>{t('svcw.price.lead')}</p>
              {completing ? <p className="dash-ai-pending-hint">{t('aiPending.priceHint')}</p> : null}
              <div className="dash-svc-setting-block"><label>{t('svcw.price.how')}</label><div className="dash-svc-choices">{PAYMENT_MODES.map((mode) => <button key={mode.value} type="button" className={paymentChoice === mode.value ? 'is-on' : ''} onClick={() => setPaymentChoice(mode.value)}><span className="dash-svc-choice-mark">{paymentChoice === mode.value ? <IconCheck size={12} /> : null}</span><span className="dash-svc-choice-copy"><strong>{t(mode.label)}</strong><small>{t(mode.hint)}</small></span></button>)}</div>
                {included ? (
                  <>
                    <MembershipInclusionsPicker projectId={projectId} offerId={service?.id ?? null} token={token} value={inclusions} onChange={setInclusions} onOpenPlans={onOpenPlans} />
                    {invalid?.field === 'amount' ? <small className="dash-svc-field-error">{invalid.message}</small> : null}
                  </>
                ) : paymentChoice !== 'free' ? <label className={`dash-svc-field dash-svc-field-inline${invalid?.field === 'amount' ? ' is-invalid' : ''}`}><span>{t('svcw.price.amount')} <b>*</b></span><span className="dash-svc-suffix"><input ref={priceRef} onFocus={() => setFocusZone('price')} onBlur={() => setFocusZone(null)} autoFocus inputMode="decimal" value={amount} placeholder="350" aria-invalid={invalid?.field === 'amount' || undefined} aria-describedby={invalid?.field === 'amount' ? 'svc-err-amount' : undefined} onChange={(event) => setAmount(event.target.value)} /><em>{currency.toUpperCase()}</em></span>{invalid?.field === 'amount' ? <small className="dash-svc-field-error" id="svc-err-amount">{invalid.message}</small> : <small>{t(paymentChoice === 'onsite' ? 'svcw.price.onsiteAmountHint' : 'svcw.price.amountHint')}</small>}</label>
                  : <p className="dash-svc-schedule-note"><IconCheck size={14} /> {t('svcw.price.noOnline')}</p>}
                {/* Mixto: además del cobro, incluida en membresías y paquetes. */}
                {canAlsoInclude ? (
                  <div className="dash-svc-mixed">
                    <div className="dash-svc-mixed-head">
                      <span>
                        <strong>{t('svcw.pay.alsoIncluded')}</strong>
                        <small>{t('svcw.pay.alsoIncludedHint')}</small>
                      </span>
                      <button type="button" role="switch" aria-checked={alsoIncluded} aria-label={t('svcw.pay.alsoIncluded')} className={`cwa-switch${alsoIncluded ? ' on' : ''}`} onClick={() => setAlsoIncluded(!alsoIncluded)}>
                        <span />
                      </button>
                    </div>
                    {alsoIncluded ? <MembershipInclusionsPicker projectId={projectId} offerId={service?.id ?? null} token={token} value={inclusions} onChange={setInclusions} onOpenPlans={onOpenPlans} /> : null}
                  </div>
                ) : null}
              </div>

                {/*
                  * Falta Stripe, y se dice AQUÍ, que es donde se elige cobrar.
                  *
                  * Sin bloquear nada: se puede seguir, terminar el alta y dejar
                  * el servicio escrito. Lo único que no se podrá es publicarlo,
                  * y decirlo en el paso donde nace la condición evita la
                  * sorpresa de encontrarse el interruptor apagado tres pasos
                  * después sin saber por qué.
                  *
                  * Y montar el servicio antes de conectar Stripe es un orden
                  * legítimo: quien está probando quiere ver su página de reserva
                  * antes de darse de alta en una pasarela de pagos.
                  */}
                {/*
                  * Dos frenos distintos, dos avisos distintos.
                  *
                  * Falta la CUENTA: es un trámite, se resuelve aquí mismo y el
                  * aviso lleva la marca de quien lo pide, con su botón. Falta el
                  * PLAN: no es un trámite, es algo que todavía no has comprado, y
                  * lo que hay que enseñar es lo que se gana con él — no una
                  * gestión de Stripe con un botón de planes pegado, que es lo que
                  * había y leía como una avería en vez de como una oferta.
                  *
                  * La misma tarjeta de Cobros y de Ventas, en su forma corta:
                  * titular, una frase y el botón. Las pastillas y la letra
                  * pequeña son el detalle de quien ya se paró a leer, y aquí se
                  * está en mitad de un alta.
                  */}
                {blockedByStripe ? (
                  canConnectStripe ? (
                    <Notice
                      tone="brand"
                      icon={<StripeMark size={17} />}
                      title={t('svcw.price.stripeTitle')}
                      actions={(
                        <button
                          type="button"
                          className="dash-secondary-btn"
                          disabled={stripe.connecting}
                          onClick={() => { void stripe.connect() }}
                        >
                          {stripe.connecting ? <IconLoader2 size={14} className="dash-spin" /> : <StripeMark size={14} />}
                          {t(stripe.connecting ? 'svcw.stripe.connecting' : 'svcw.stripe.connect')}
                        </button>
                      )}
                    >
                      <p>{t('svcw.stripe.priceBody')}</p>
                    </Notice>
                  ) : (
                    <UpsellCard slim standalone copy={PAYMENTS_UPSELL} onOpenPlans={handleOpenPlans} />
                  )
                ) : null}
            </section> : null}

            {step === 6 ? <section className="dash-svc-panel-step">
              <span className="dash-svc-step-kicker">{t('offerFields.kicker')}</span>
              <h2>{t('offerFields.title')}</h2>
              <p>{t('offerFields.lead')}</p>
              <OfferCustomFieldsEditor projectId={projectId} token={token} kind="bookings" values={customValues} onChange={setCustomValues} />
            </section> : null}

            {step === 7 ? <section className="dash-svc-panel-step" ref={stepPanelRef} tabIndex={-1}>
              <span className="dash-svc-step-kicker">{t('svcw.review.kicker')}</span><h2>{t('svcw.review.title')}</h2><p>{t('svcw.review.lead')}</p>
              <div className="dash-svc-review">
                <div><span>{t('svcw.review.service')}</span><strong>{title.trim()}</strong><button type="button" onClick={() => setStep(0)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('offers.new.shortDescriptionLabel')}</span><strong className="dash-svc-review-excerpt">{shortDescription.trim() || <em>{t('offers.new.review.noDescription')}</em>}</strong><button type="button" onClick={() => setStep(0)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('offers.new.longDescriptionLabel')}</span><strong className="dash-svc-review-excerpt">{description.trim() || <em>{t('offers.new.review.noDescription')}</em>}</strong><button type="button" onClick={() => setStep(0)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('svcw.review.duration')}</span><strong><IconClock size={15} /> {lengthLabel(t)}</strong><button type="button" onClick={() => setStep(1)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('svcw.review.capacity')}</span><strong><IconUsers size={15} /> {t(capacity === 1 ? 'svcw.person.one' : 'svcw.person.many', { count: capacity })}</strong><button type="button" onClick={() => setStep(1)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('svcw.review.payment')}</span><strong>{priceLabel}</strong><button type="button" onClick={() => setStep(2)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('svcw.review.schedule')}</span><strong><IconCalendarEvent size={15} /> {t('svcw.review.openDays', { count: new Set(rules.map((rule) => rule.weekday)).size })}</strong><button type="button" onClick={() => setStep(4)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('svcw.review.cancel')}</span><strong>{cancelWindow && Number(cancelWindow) > 0 ? t('svcw.cancel.beforeAmount', { amount: humanMinutes(String(Number(cancelWindow) * 60)) }) : t('svcw.cancel.noLimit')}</strong><button type="button" onClick={() => setStep(5)}>{t('svcw.review.edit')}</button></div>
                <div><span>{t('svcw.review.reminder')}</span><strong><IconBell size={15} /> {reminderEnabled ? t('svcw.cancel.beforeAmount', { amount: humanMinutes(String((Number(reminderHours) || 24) * 60)) }) : t('svcw.set.reminderOff')}</strong><button type="button" onClick={() => setStep(1)}>{t('svcw.review.edit')}</button></div>
              </div>

              {/*
                * Publicar o dejarlo en borrador, aquí y no después.
                *
                * Era una frase que informaba —«se creará como borrador»— y no
                * una decisión, así que quien ya tenía claro que quería el
                * servicio en línea terminaba el asistente, buscaba el servicio
                * en la lista y volvía a entrar para pulsar Publicar. Tres pasos
                * para algo que ya sabía en el segundo.
                *
                * Borrador sigue siendo lo que viene puesto: revisar la página
                * antes de enseñarla es lo prudente, y lo prudente es lo que debe
                * pasar cuando alguien no decide nada.
                */}
              {/*
                * Un segmentado, no un interruptor.
                *
                * «Se creará como borrador» con una palanca al lado se lee como
                * una afirmación con un botón encima: no dice cuál es la otra
                * opción, ni siquiera que exista otra. Con los dos estados
                * escritos y uno marcado, la elección se ve entera de un vistazo
                * — y es el mismo mando que ya usa «Individual / Grupal» dos
                * pasos antes, así que no hay nada nuevo que aprender.
                */}
              <div className="dash-svc-setting-block dash-svc-publish-block">
                <label id="publish-label">{t(editing ? 'svcw.review.onSave' : 'svcw.review.onCreate')}</label>
                <div
                  className={`dash-svc-segmented${publish ? ' is-second' : ''}`}
                  role="radiogroup"
                  aria-labelledby="publish-label"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!publish}
                    className={!publish ? 'is-on' : ''}
                    disabled={saving}
                    onClick={() => { setPublish(false) }}
                  ><IconPencil size={14} /> {t('svcw.review.draft')}</button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={publish}
                    className={publish ? 'is-on' : ''}
                    disabled={saving}
                    onClick={() => {
                      /* El cupo antes que Stripe, como en `publishBlockFor`: el
                         orden decide qué ventana sale, y al revés la misma
                         situación se explicaría de dos maneras según se entre
                         por aquí o por la tabla. */
                      if (publishBlockedByPlan) {
                        setLimitDialog(true)
                        return
                      }
                      if (blockedByStripe) {
                        setPublishBlockDialog(canConnectStripe ? 'stripe' : 'plan')
                        return
                      }
                      setPublish(true)
                    }}
                  ><IconWorld size={14} /> {t('svcw.review.published')}</button>
                </div>
                <small>
                  {t(publish ? 'svcw.review.publishedHint' : 'svcw.review.draftHint')}
                </small>
              </div>
            </section> : null}
            {/* Sólo lo que NO tiene campo al que señalar —el horario, un fallo
                del servidor—. Lo demás sale junto a su campo, que es donde hay
                que hacer algo. */}
            {invalid && !invalid.field.match(/^(title|amount|duration|contactEmail)$/)
              ? <p className="dash-svc-error" role="alert">{invalid.message}</p>
              : null}
            {error || stripe.error ? <p className="dash-svc-error" role="alert">{error || stripe.error}</p> : null}
            {/* Al pie del formulario y no en la cabecera: es donde está la
                mirada al terminar de rellenar el paso, y de paso deja de haber
                dos juegos de botones —uno de escritorio y otro de móvil— que
                decían cosas distintas. */}
            {/*
              Editando se guarda desde cualquier paso.

              El alta es una secuencia: los seis pasos construyen algo que aún no
              existe, así que el botón final va al final. Editando no —se entra a
              cambiar una cosa concreta— y obligar a recorrer hasta «Revisión»
              para confirmar un precio es pedir cinco clics por una edición.

              «Siguiente» sigue estando, en secundario: leer el resto de los pasos
              también es una forma legítima de editar.
            */}
            <div className="dash-svc-step-actions">
              {editing ? (
                /* Editando, el pie sólo navega: guardar y salir viven arriba, en
                   la barra, donde están a la vista desde cualquier paso sin
                   tener que bajar hasta el final de un formulario largo. */
                <>
                  {step > 0 ? (
                    <button type="button" className="dash-secondary-btn" disabled={saving} onClick={() => { setError(''); setStep(step - 1) }}><IconArrowLeft size={14} /> {t('svcw.back')}</button>
                  ) : null}
                  {step < STEPS.length - 1 ? (
                    <button type="button" className="dash-secondary-btn" onClick={next}>{t('svcw.next')} <IconArrowRight size={14} /></button>
                  ) : (
                    /*
                      Y al final del recorrido, la salida.

                      Guardar vive arriba y desde cualquier paso, que es lo que
                      hace falta para venir a cambiar UNA cosa. Pero quien
                      recorre los siete llega al resumen y ahí se acaba la
                      pantalla: el único botón era «Atrás», así que había que
                      subir a buscar el de guardar justo cuando se acaba de
                      terminar. Es el mismo `create`, no otro camino.

                      Sin cambios también se ofrece: guardar lo que ya está no
                      rompe nada, y un botón apagado en el último paso se lee
                      como «algo va mal» en vez de «no hace falta».
                    */
                    <button type="button" className="dash-primary-btn" disabled={saving} onClick={() => { void create() }}>
                      {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
                      {t(saving ? 'svcw.saving' : 'svcw.saveAndClose')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {step > 0
                    ? <button type="button" className="dash-secondary-btn" disabled={saving} onClick={() => { setError(''); setStep(step - 1) }}><IconArrowLeft size={14} /> {t('svcw.back')}</button>
                    : <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('common.cancel')}</button>}
                  {step < STEPS.length - 1
                    ? <button type="button" className="dash-primary-btn" onClick={next}>{t('svcw.next')} <IconArrowRight size={14} /></button>
                    : <button type="button" className="dash-primary-btn" disabled={saving} onClick={() => { void create() }}>{saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}{t(saving ? 'svcw.saving' : publish ? 'svcw.createPublish' : 'svcw.createService')}</button>}
                </>
              )}
            </div>
          </div>
        </form>

        <aside className="dash-svc-wizard-preview" aria-label={t('svcw.previewAria')}>
        {/*
          En el paso del horario, la maqueta se aparta.

          Ahí no se está decidiendo cómo SE VE la página sino qué huecos produce
          el horario, y eso no se comprueba en una miniatura al 40%: hay que
          poder recorrer los días y contar las horas. Se cambia por el probador,
          a tamaño real y tocable.

          En «Revisión» vuelve la maqueta, que es cuando sí toca mirar la página.
        */}
        {step === 4 ? (
          <div className="dash-svc-tester">
            <div className="dash-svc-preview-heading">
              <span>{t('svcw.tester')}</span>
              <em>{t('svcw.testerHint')}</em>
            </div>
            {/* Dentro del mismo marco de navegador que la maqueta: es la misma
                página, sólo que a tamaño real y tocable en vez de reducida. Sin
                el marco parecía otra pantalla del panel. */}
            <div className="dash-svc-browser is-live">
              <div className="dash-svc-browser-bar"><i /><i /><i /><span className="dash-svc-browser-url">{host}/book/{slug}</span></div>

            {scheduleValid && availableDays.length ? (
              /*
               * La misma pieza que sirve la página, no una parecida.
               *
               * Rótulo, título, la caja con la fecha arriba y la tira debajo, y
               * las horas en su fila: si el probador dibuja otra cosa, deja de
               * probar nada — se comprueba una maqueta y se publica otra.
               *
               * El acento sale del servicio cuando ya tiene uno; creando todavía
               * no hay, así que se usa el del panel.
               */
              <div className="dash-svc-tester-body" ref={bodyRef} style={{ '--tester-accent': acentoVisible } as CSSProperties}>
                <p className="dash-svc-tester-eyebrow">{t('svcw.tester.eyebrow')}</p>
                <h3 className="dash-svc-tester-title">{t('svcw.tester.title')}</h3>
                <p className="dash-svc-tester-sub">{t('svcw.tester.sub')}</p>

                <div className="dash-svc-tester-card">
                  <div className="dash-svc-tester-head">
                    {/* Tres desplegables de verdad, como en la página: el día
                        salta a esa fecha, el mes y el año al primer hueco que
                        tengan. */}
                    <select value={shownDay?.key ?? ''} onChange={(event) => setPreviewDay(event.target.value)} aria-label={t('svcw.sch.day')}>
                      {availableDays.filter((entry) => entry.key.slice(0, 7) === (shownDay?.key ?? '').slice(0, 7))
                        .map((entry) => <option key={entry.key} value={entry.key}>{dayLabel(entry.key, 'day')}</option>)}
                    </select>
                    <span>{t('svcw.tester.of')}</span>
                    <select
                      value={(shownDay?.key ?? '').slice(0, 7)}
                      onChange={(event) => setPreviewDay(availableDays.find((entry) => entry.key.startsWith(event.target.value))?.key ?? '')}
                      aria-label={t('svcw.tester.month')}
                    >
                      {[...new Set(availableDays.map((entry) => entry.key.slice(0, 7)))].map((ym) => (
                        <option key={ym} value={ym}>{dayLabel(`${ym}-01`, 'monthLong')}</option>
                      ))}
                    </select>
                    <span>{t('svcw.tester.of')}</span>
                    <select
                      value={(shownDay?.key ?? '').slice(0, 4)}
                      onChange={(event) => setPreviewDay(availableDays.find((entry) => entry.key.startsWith(event.target.value))?.key ?? '')}
                      aria-label={t('svcw.tester.year')}
                    >
                      {[...new Set(availableDays.map((entry) => entry.key.slice(0, 4)))].map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  {/* Flechas a los lados y cinco días repartidos, como en la
                      página: allí la tira es una rejilla de tres columnas con el
                      desplazamiento en medio, no una fila que se arrastra. */}
                  <div className="dash-svc-tester-slider">
                    <button type="button" className="dash-svc-tester-nav" aria-label={t('svcw.tester.prevDays')}
                      onClick={() => { const strip = stripRef.current; if (strip) strip.scrollBy({ left: -stripPage(strip), behavior: 'smooth' }) }}>
                      <IconArrowLeft size={16} />
                    </button>
                  <div className="dash-svc-tester-strip" ref={stripRef}>
                    {availableDays.map((entry) => (
                      <button
                        type="button"
                        key={entry.key}
                        className={entry.key === shownDay?.key ? 'is-on' : ''}
                        data-day={entry.key}
                        onClick={() => setPreviewDay(entry.key)}
                      >
                        <small>{dayLabel(entry.key, 'weekday')}</small>
                        <strong>{dayLabel(entry.key, 'day')}</strong>
                        <small>{dayLabel(entry.key, 'month')}</small>
                      </button>
                    ))}
                  </div>
                    <button type="button" className="dash-svc-tester-nav" aria-label={t('svcw.tester.nextDays')}
                      onClick={() => { const strip = stripRef.current; if (strip) strip.scrollBy({ left: stripPage(strip), behavior: 'smooth' }) }}>
                      <IconArrowRight size={16} />
                    </button>
                  </div>
                </div>

                {shownDay ? (
                  <>
                    <p className="dash-svc-tester-daytitle">{t('svcw.tester.pickTime')}</p>
                    {/* Libres y ocupadas en la misma rejilla y en orden de reloj:
                        separadas en dos listas se pierde lo único que importa
                        aquí, que es a qué hora se acaba el hueco. */}
                    <div className="dash-svc-tester-timeswrap" ref={timesRef}>
                    {/*
                      El banner va a la raíz del documento, no aquí dentro.
                      El cuerpo del probador recorta, y la rejilla de horas mide
                      poco más que el propio banner: dibujándolo dentro sólo cabía
                      centrado en la rejilla —nunca sobre su hora— y aun así se
                      cortaba por la izquierda. Fuera, se coloca respecto a la
                      pantalla y no lo recorta nadie.
                    */}
                    {peeked && peek ? createPortal(
                      <>
                        <i className={peek.below ? 'dash-svc-tester-peek-arrow is-below' : 'dash-svc-tester-peek-arrow'}
                          style={{ '--peek-x': `${peek.arrow}px`, '--peek-t': `${peek.top}px`, '--peek-b': `${peek.bottom}px`, '--tester-accent': acentoVisible } as CSSProperties} />
                        <div className={peek.below ? 'dash-svc-tester-peek is-below' : 'dash-svc-tester-peek'} role="status"
                          style={{ '--peek-x': `${peek.x}px`, '--peek-t': `${peek.top}px`, '--peek-b': `${peek.bottom}px`, '--tester-accent': acentoVisible } as CSSProperties}>
                          <span className="dash-svc-tester-peek-top">
                            <strong>{peeked.name}</strong>
                            <span className="dash-svc-tester-peek-tag" data-status={peeked.status}>{t(TAKEN_LABEL[peeked.status])}</span>
                          </span>
                          <span className="dash-svc-tester-peek-when">{peeked.time} – {peeked.endTime}</span>
                          {peeked.email ? <span>{peeked.email}</span> : null}
                          {peeked.phone ? <span>{peeked.phone}</span> : null}
                        </div>
                      </>,
                      document.body,
                    ) : null}
                    <div className="dash-svc-tester-times">
                      {[
                        ...shownDay.times.map((time) => ({ time, taken: null as TakenSlot | null })),
                        ...shownDay.taken.map((taken) => ({ time: taken.time, taken })),
                      ]
                        .sort((a, b) => a.time.localeCompare(b.time))
                        .map(({ time, taken }) => (taken ? (
                          <button
                            type="button"
                            className={peeked?.id === taken.id ? 'is-taken is-peek' : 'is-taken'}
                            key={taken.id}
                            onMouseEnter={(event) => openPeek(taken.id, event.currentTarget)}
                            onMouseLeave={() => closePeek(taken.id)}
                            onFocus={(event) => openPeek(taken.id, event.currentTarget)}
                            onBlur={() => closePeek(taken.id)}
                            onClick={(event) => openPeek(taken.id, event.currentTarget)}
                          >
                            {taken.time}
                          </button>
                        ) : <span key={time}>{time}</span>))}
                    </div>
                    </div>
                    {/* Sólo el probador lleva esta línea: la página no cuenta sus
                        huecos, y aquí es justo lo que se viene a saber. */}
                    <p className="dash-svc-tester-sum">
                      {t(shownDay.times.length === 1 ? 'svcw.tester.freeOne' : 'svcw.tester.freeMany', { count: shownDay.times.length })}
                      {shownDay.taken.length
                        ? t(shownDay.taken.length === 1 ? 'svcw.tester.bookedOne' : 'svcw.tester.bookedMany', { count: shownDay.taken.length })
                        : ''}
                      {capacity > 1 ? t('svcw.tester.seatsEach', { count: capacity }) : ''}
                      {t('svcw.tester.inDays', { count: availability.length, days: availableDays.length })}
                    </p>
                  </>
                ) : null}
              </div>
            ) : (
              <p className="dash-svc-tester-empty">
                {t(scheduleValid ? 'svcw.tester.noSlots' : 'svcw.tester.openADay')}
              </p>
            )}
            </div>
          </div>
        ) : (
        <>
          <div className="dash-svc-preview-heading"><span>{t('svcw.preview')}</span><em>{t('svcw.previewHint')}</em></div>
          <div className={`dash-svc-browser is-zoom-${zoom}`} data-focus={focusZone ?? undefined}><div className="dash-svc-browser-bar"><i /><i /><i /><span className="dash-svc-browser-url">{host}/book/{slug}</span></div>
          {/* Dos capas: el marco que RECORTA y no se mueve, y la página que se
              escala dentro. Sin el recorte, acercarse estiraba el marco y se
              leía como una página más larga en vez de como un acercamiento. */}
          <div className="dash-svc-page-viewport" ref={viewportRef}><div className="dash-svc-page-scale" ref={scaleRef}><div
            className="dash-svc-page"
            /*
             * El color elegido, redefinido AQUÍ dentro.
             *
             * La maqueta pinta sus botones y la hora elegida con `--dash-accent`,
             * que es el acento del panel. Cambiando la variable sólo en esta caja,
             * todo lo de dentro se repinta sin tocar una sola de sus reglas —y sin
             * que el color de este servicio se escape al resto del panel—.
             */
            style={{ '--dash-accent': acentoVisible } as CSSProperties}
          >
            {/* El MISMO orden que la página publicada, que es para lo que esta
                maqueta existe: eyebrow, título, cobro, portada, descripción y
                datos. Así se arma la columna en `bookingPage.ts`, en el worker.

                Estaba con la portada en banda arriba del todo y el cobro al
                final —las dos justo donde la página real NO las pone—, y era lo
                que hacía que la vista previa no se pareciera a lo que se iba a
                publicar. */}
            <div className="dash-svc-page-info" data-zone="info">
              <span className="dash-svc-page-eyebrow">{t('svcw.page.book')}</span>{/* Fantasmas, no texto de relleno.
                  Decía «Nombre del servicio» y «La descripción aparecerá aquí»,
                  y eso se lee como si la página YA dijera eso: más de una vez
                  alguien deja el asistente creyendo que ese texto es el que se
                  va a publicar. Una barra gris no se puede confundir con
                  contenido — dice «aquí va algo» sin fingir que ya está. */}
              {title.trim()
                ? <strong className="dash-svc-page-title" data-zone="title">{title.trim()}</strong>
                : <span className="dash-svc-ghost is-title" data-zone="title" aria-hidden="true" />}
              {shortDescription.trim()
                ? <p className="dash-svc-page-desc" data-zone="desc">{shortDescription.trim()}</p>
                : <span className="dash-svc-ghost-lines" data-zone="desc" aria-hidden="true"><i /><i /><i /></span>}
              {/*
                * El cobro no dice nada hasta que se decide.
                *
                * `paymentMode` arranca en «en el lugar», así que la ficha
                * anunciaba «Sin cobro en línea» desde el primer paso — una
                * afirmación sobre algo que nadie ha elegido todavía, y de las
                * más fáciles de creerse: quien no llegue a mirar el paso de
                * Precio publica un servicio convencido de que decidió eso.
                *
                * Hasta entonces, su fantasma: dice que ahí va algo sin decir
                * qué. Y la zona sigue existiendo, así que la cámara la
                * encuentra igual al llegar al paso.
                */}
              {priceDecided
                ? <span className="dash-svc-page-price" data-zone="price">{priceLabel}</span>
                : <span className="dash-svc-ghost is-price" data-zone="price" aria-hidden="true" />}
              {/* La portada, debajo del cobro y no en una banda a sangre arriba:
                  es donde cae en la página real. La zona de la cámara va en la
                  propia imagen y no en un marcador aparte, así mide donde esté
                  sin depender de un alto escrito a mano. */}
              {imageUrl
                ? <PreviewGallery images={images} />
                : <span className="dash-svc-ghost is-image" data-zone="media" aria-hidden="true" />}
              {/* Duración y cupo, en esqueleto hasta que se pasa por Ajustes: arrancan
                  en «1 h» y «Cita individual», y eso son dos afirmaciones sobre
                  algo que nadie ha elegido. Es el mismo caso que el cobro y las
                  fechas. */}
              {settingsDecided
                ? <ul className="dash-svc-page-facts" data-zone="facts"><li><IconClock size={11} /> {lengthLabel(t)}</li><li><IconUsers size={11} /> {capacity === 1 ? t('svcw.page.oneToOne') : t('svcw.page.spots', { count: capacity })}</li></ul>
                : <span className="dash-svc-ghost-row is-facts" data-zone="facts" aria-hidden="true"><i /><i /></span>}
            </div>
            <div className="dash-svc-page-form">
              {/* Los campos con su nombre, no dos rectángulos: es la única forma
                  de ver si lo que se pide es lo que se quería pedir. */}
              <div className="dash-svc-page-fields" data-zone="form">
                {/* Con su dibujo delante, como en la página de verdad: un
                    recuadro con una palabra dentro se lee como una etiqueta; con
                    el icono se lee como un campo que hay que rellenar, que es lo
                    que la maqueta tiene que enseñar. */}
                <span className="dash-svc-page-field"><IconUser size={10} aria-hidden="true" />{t('svcw.form.name')}</span>
                <span className="dash-svc-page-field"><IconAt size={10} aria-hidden="true" />{t('svcw.form.email')}</span>
                <span className="dash-svc-page-field"><IconPhone size={10} aria-hidden="true" />{t('svcw.form.phone')}</span>
                {formFields.filter((field) => field.label.trim()).map((field) => {
                  /* El mismo mapa que usa el editor de preguntas, importado y no
                     copiado: con dos tablas, añadir un tipo dejaría un hueco en
                     una de las dos y nadie se enteraría hasta verlo. */
                  const Dibujo = TYPE_ICON[field.type]
                  return (
                    <span className="dash-svc-page-field is-custom" key={field.name}>
                      <Dibujo size={10} aria-hidden="true" />
                      {field.label}{field.required ? <i>*</i> : null}
                    </span>
                  )
                })}
              </div>
              {/* Días y horas, envueltos: la zona tiene que abarcar las dos, no
                  la columna entera —al subir el formulario, apuntar al bloque
                  completo llevaba la cámara a los campos— ni sólo la etiqueta,
                  que centraría en dos palabras. */}
              {/*
                El horario, como está hoy la página de verdad.

                Se quedó atrás: aquélla perdió los rótulos «Elige el día» y
                «Elige la hora» —un calendario con días apagados y una fila de
                horas no necesitan que les pongan nombre— y ganó dos cajas con
                filete, la fecha como título con sus tres desplegables y
                «Selecciona un horario» encabezando las horas.

                Y va toda seguida. La página real parte esto en un segundo paso,
                detrás de un botón de «Continuar»; aquí se salta ese corte y se
                enseña todo de una vez, que es lo que hay que poder revisar
                mientras se configura.
              */}
              <div className="dash-svc-page-schedule" data-zone="schedule">
                {/* La fecha va en su banda gris y los días debajo, dentro de
                    la MISMA caja: así está en la página de verdad, y una maqueta
                    que dibuja otra cosa deja de comprobar nada. */}
                <div className="dash-svc-page-box is-picker">
                  <span className="dash-svc-page-datetitle">
                    {/*
                        El calendario no espera al paso del horario.

                        El fantasma existe para lo que se AFIRMA sin haberlo
                        elegido —el precio, la duración—, y aquí no se afirma
                        nada: los días salen de las reglas que el servicio YA
                        tiene, y si no hay ninguna válida no hay días y el hueco
                        aparece solo. Atado al paso, la ficha se pasaba los tres
                        primeros con cuatro barras grises justo donde va la mitad
                        de la página, y no se veía qué se estaba construyendo.
                    */}
                    {shownDay
                      ? <>
                          {/* Las puntas van dibujadas en CSS y no con el signo
                              «⌄»: ese glifo se apoya en la línea base y queda
                              colgando por debajo del texto, y cuánto depende de
                              la tipografía. Un cuadrado girado siempre cae donde
                              se le dice. */}
                          {dayLabel(shownDay.key, 'day')} <i aria-hidden="true" /> {t('svcw.page.of')} {dayLabel(shownDay.key, 'monthLong')} <i aria-hidden="true" /> {shownDay.key.slice(0, 4)} <i aria-hidden="true" />
                        </>
                      : <span className="dash-svc-ghost" aria-hidden="true">{t('svcw.page.date')}</span>}
                  </span>
                  {/* Días de verdad, y pulsables: la maqueta deja de ser un
                      dibujo y pasa a ser la comprobación —se toca un día y salen
                      SUS horas, que es lo único que contesta «¿en qué quedó mi
                      horario?». */}
                  <div className="dash-svc-page-days">
                    {availableDays.length
                      ? availableDays.slice(0, 5).map((entry) => (
                        <button
                          type="button"
                          key={entry.key}
                          className={entry.key === shownDay?.key ? 'is-on' : ''}
                          onFocus={() => setFocusZone('schedule')}
                          onBlur={() => setFocusZone(null)}
                          onClick={() => setPreviewDay(entry.key)}
                        >
                          {dayLabel(entry.key, 'weekday')}<b>{dayLabel(entry.key, 'day')}</b>{dayLabel(entry.key, 'month')}
                        </button>
                      ))
                      : <span className="dash-svc-ghost-row" aria-hidden="true"><i /><i /><i /><i /></span>}
                  </div>
                </div>
                {/* Las horas NO van en caja: en la página son un título y una
                    fila de fichas sueltas sobre el fondo. Metidas en un recuadro
                    parecían un segundo apartado al mismo nivel que el
                    calendario, cuando cuelgan del día que se acaba de elegir. */}
                <div className="dash-svc-page-times">
                  <span className="dash-svc-page-datetitle is-small">{t('svcw.tester.pickTime')}</span>
                  <div className="dash-svc-page-slots">
                    {shownDay
                      ? shownDay.times.map((slot, index) => (
                        <span key={slot} className={index === 0 ? 'is-on' : ''}>{slot}</span>
                      ))
                      : <span className="dash-svc-ghost-row" aria-hidden="true"><i /><i /><i /></span>}
                  </div>
                  {/* El recuento: es lo que de verdad se quiere saber al tocar un
                      margen —cuántos huecos quedan— y contarlos a ojo en una
                      maqueta pequeña no se puede. */}
                  {shownDay ? (
                    <span className="dash-svc-page-count">{t('svcw.page.slotsTotal', { count: shownDay.times.length, total: availability.length })}</span>
                  ) : null}
                </div>
              </div>
              <b className="dash-svc-page-cta">{t(paymentMode === 'onsite' || included ? 'svcw.page.cta.book' : 'svcw.page.cta.pay')}</b>
            </div>
            {description.trim() ? <section className="dash-svc-page-details" data-zone="details">
              <b>{t('offers.new.longDescriptionLabel')}</b><p>{description.trim()}</p>
            </section> : null}
          </div></div></div></div><p className="dash-svc-preview-note">
            {editing || step === STEPS.length - 1
              /* Editando siempre está completa, así que la frase de «en el
                 último paso la verás entera» sobra: prometía algo que ya pasó. */
              ? t('svcw.preview.noteFull')
              : t('svcw.preview.noteZoom')}
          </p>
        </>
        )}
        </aside>
      </div>
      {/* La misma ventana de espera que Configuración → Stripe, no una copia: el
          alta ocurre fuera y lo que hay que decir mientras tanto es idéntico.
          Se cierra sola al quedar la cuenta lista —el sondeo de arriba— y a mano
          con «Ya terminé». */}
      {stripe.awaiting ? (
        <StripeOnboardingDialog
          status={stripe.status}
          onClose={stripe.dismiss}
        />
      ) : null}


      {/* La misma ventana que sale al toparse con el tope de páginas o de
          ofertas de la tienda: es el mismo tope, así que se ve igual. */}
      {limitDialog ? (
        <PlanLimitModal
          resource={t('usage.stat.offers').toLowerCase()}
          kind="offers"
          currentPlan={plan}
          onClose={() => setLimitDialog(false)}
          onOpenPlans={() => { setLimitDialog(false); onOpenPlans?.() }}
        />
      ) : null}

      {publishBlockDialog ? (
        <PublishBlockDialog
          kind={publishBlockDialog}
          connecting={stripe.connecting}
          onConnect={() => { void stripe.connect() }}
          onOpenPlans={handleOpenPlans}
          onClose={() => setPublishBlockDialog(null)}
        />
      ) : null}
    </div>, document.body,
  )
}
